import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import { PaymentService } from '../../shared/services/payment.service';
import { CartService } from '../../shared/services/cart.service';
import { NotificationService } from '../../shared/services/notification.service';
import { DocumentService } from '../../shared/services/document.service';
import { DocumentPresignedType } from '../../shared/services/api.service';
import { formatAddress, hasAddress } from '../../shared/utils/address.utils';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  image: string;
  grade: string;
  brand: string;
  model: string;
  year: string;
  engine?: string;
  seller?: any;
  selectedForCancel?: boolean;
  selectedReason?: string;
  cancellationComment?: string;
  refundAmount?: number;
  // Return properties
  selectedForReturn?: boolean;
  returnReason?: string;
  returnComment?: string;
  returnRefundAmount?: number;
  uploadedFiles?: File[];
  uploadedFileUrls?: string[];
  invoiceNumber?: string;
  quantity?: string;
  part_no?: string;
  available_quantity: number;
  total_quantity: string;
  item_id: any;
  booking_amount?: any;
  integration_id?: any;
}

interface OrderStatus {
  stage:
  | 'Order Placed'
  | 'Order Ready'
  | 'Shipped'
  | 'In-Transit'
  | 'Out for Delivery'
  | 'Delivered'
  | 'Order Cancelled'
  | 'Return Requested'
  | 'Picked Up'
  | 'Refund Credited'
  | 'Payment Pending'
  | 'Payment Successful'
  | 'Payment Failed';
  isCompleted: boolean;
  date: string;
  description: string;
  timestamp: string;
  icon: string;
}

interface Address {
  name: string;
  pincode: string;
  address: string;
  gst?: string;
  type?: 'Home' | 'Work' | 'Other';
}

interface PriceBreakdown {
  subtotal: number;
  discount: number;
  platformCharges: number;
  deliveryCharges: number;
  gst: number;
  total: number;
  scrap_booking_amount?: number;
  base_price?: number;
  gst_amount?: number;
  platform_charges?: number;
  delivery_charges?: number;
  order_total?: number;
  tcs_amount?: number;
  pending_amount?: number;
  remaining_booking_amount?: number;
  paid_booking_amount?: number;
  taxable_value?: number;
  rounded_total?: number;
  roundoff_amount?: number;
  total_amount?: number;
  total_discount?: number;
  direct_discount?: number;
  offer_discount?: number;
  delivery_charges_with_gst?: number;
  delivery_gst_amount?: number;
  delivery_gst_percent?: number;
  gst_percentage?: number;
  tcs_percent?: number;
  [key: string]: any;
}

interface CancellationReason {
  value: string;
  label: string;
}

interface ReturnReason {
  value: string;
  label: string;
  refundPercentage: number;
  message?: string;
}

interface OrderDetail {
  id: string;
  invoiceNo: string;
  orderNo: string;
  estimatedDelivery: string;
  status: OrderStatus[];
  items: OrderItem[];
  billingAddress: Address;
  shippingAddress: Address;
  sellerAddress?: Address;
  storeAddress?: Address;
  priceBreakdown: PriceBreakdown;
  canCancel: boolean;
  isStorePickup?: boolean;
  invoicePdfPath?: string;
  invoiceRating?: number;
  invoice_status?: string;
  generated_pin?: any;
  isBooking?: boolean;
  isEnquiry?: boolean;
  bookingDate?: string;
}

// API Response interfaces
interface ApiOrderItem {
  booking_amount: string;
  quality: string;
  make: string;
  model: string;
  year_of_manufacturing: string;
  id: string;
  part_desc: string;
  item_amount: string;
  image_url: string;
  invoice_number: string;
  quantity: string;
  part_no: string;
  available_quantity: number;
  total_quantity: string;
  item_id: string;
  integration_id: any;
}

interface ApiAddress {
  id: number;
  order_id_id: number;
  seller_id: number | null;
  phone_no: string;
  address_1: string;
  address_2: string;
  address_3: string;
  district: string;
  taluka: string;
  state: string;
  pincode: string;
  address_id: number | null;
  created_dt: string;
  update_dt: string;
  is_active: boolean;
  city: string;
  land_mark: string;
  region: string;
  country: string;
  state_code: string;
  is_gstin_no: boolean;
  gstin_number: string;
  address_type: string;
  order_address_type: string;
  address_type_name: string | null;
  receiver_name: string;
  receiver_phone_no: string;
  company_name: string;
}

interface ApiPriceBreakup {
  sub_total: string;
  discount_on_base_price: string;
  platform_charges: string;
  delivery_charges: string;
  gst: string;
  total_amount: string;
  scrap_booking_amount?: string;
}

interface ApiSellerDetail {
  seller_code: string;
  seller_name: string;
  branch_code: string;
  branch_name: string;
  address_1: string;
  address_2: string;
  address_3: string;
  district: string;
  taluka: string;
  state: string;
  region: string;
  pincode: string;
  city: string;
  is_parent: boolean;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  image_path: string;
}

interface ApiOrderResponse {
  invoice_number: string;
  order_number: string;
  invoice_status: string;
  delivery_date: string;
  delivery_type: string;
  tracking_dt: string;
  order_rating_status: string;
  invoice_rating: number;
  // The invoice list payload does not always include these, so they are optional
  invoice_items?: ApiOrderItem[];
  invoice_amount: string | number;
  overall_invoice_amount?: number;
  delivery_charges?: number;
  seller_detail?: ApiSellerDetail;
  price_breakup?: ApiPriceBreakup;
  order_address?: ApiAddress[];
  invoice_pdf_path: string;
  total_quantity: string;
  booking_amount: any;
  generated_pin: any;
  integration_id: any;
}

interface PendingPaymentPartDetail {
  part_name: string;
  category: string;
  sub_category: string;
}

interface PendingPaymentInvoice {
  invoice_id: number;
  booking_number: string;
  payment_amount: number;
  part_details: PendingPaymentPartDetail[];
}

@Component({
  selector: 'app-orders-detail-summary',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumComponent],
  templateUrl: './orders-detail-summary.component.html',
  styleUrl: './orders-detail-summary.component.scss',
})
export class OrdersDetailSummaryComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @ViewChild('popupTimelineItems', { static: false })
  popupTimelineItems?: ElementRef;
  @ViewChild('popupTimelineContainer', { static: false })
  popupTimelineContainer?: ElementRef;

  private isMeasuringHeights = false;
  private measurementScheduled = false;
  private lastStatusListLength = 0;

  ngAfterViewInit(): void {
    // Measure heights after initial view init if popup is already open
    if (this.showOrderUpdatesPopup && this.popupStatusList.length > 0) {
      this.scheduleMeasurement();
    }
  }

  ngAfterViewChecked(): void {
    // Re-measure when popup is open and status list length changes
    if (this.showOrderUpdatesPopup && this.popupStatusList.length > 0) {
      const currentLength = this.popupStatusList.length;
      if (
        currentLength !== this.lastStatusListLength &&
        !this.measurementScheduled
      ) {
        this.lastStatusListLength = currentLength;
        this.scheduleMeasurement();
      }
    } else {
      this.lastStatusListLength = 0;
    }
  }

  private scheduleMeasurement(): void {
    if (this.measurementScheduled || this.isMeasuringHeights) return;

    this.measurementScheduled = true;
    // Use double requestAnimationFrame to ensure DOM is fully rendered
    // First frame: browser has painted, second frame: ensures all layout calculations are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.measurePopupTimelineHeights();
        this.measurementScheduled = false;
      });
    });
  }

  // Format timestamp for display in popup
  formatTimestampForDisplay(timestamp: string): string {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return timestamp;
    }
  }

  // Popup timeline height calculations - dynamic based on actual content
  getPopupTimelineLineHeight(): string {
    // Use measured height if available
    if (this.popupTimelineLineHeight !== '0px') {
      return this.popupTimelineLineHeight;
    }

    // Calculate fallback if measurement hasn't run yet
    const statusCount = this.popupStatusList?.length || 0;
    if (statusCount <= 1) return '0px';

    // Use responsive calculation with better accuracy
    const isMobile = window.innerWidth < 768;
    const spacing = isMobile ? 16 : 20;
    const iconSize = isMobile ? 24 : 32;
    const textHeight = isMobile ? 75 : 85;
    // Account for first icon center position (approximately iconSize/2 + padding)
    const firstIconCenterOffset = isMobile ? 12 : 16; // Approximate first icon center from top
    const gapPerItem = spacing + iconSize + textHeight;
    // Line starts at 28px, so we need to calculate from first icon center to last icon center
    // Then subtract the offset from line start to first icon center
    const totalHeight =
      (statusCount - 1) * gapPerItem + firstIconCenterOffset - 28;
    return `${Math.max(0, totalHeight)}px`;
  }

  getPopupTimelineProgressHeight(): string {
    // Use measured height if available
    if (this.popupTimelineProgressHeight !== '0px') {
      return this.popupTimelineProgressHeight;
    }

    // Calculate fallback if measurement hasn't run yet
    if (!this.popupStatusList || this.popupStatusList.length === 0)
      return '0px';
    const completed = this.popupStatusList.filter(
      (s: OrderStatus) => s.isCompleted,
    ).length;
    const total = this.popupStatusList.length;
    if (total === 0 || completed === 0) return '0px';
    if (completed >= total) return this.getPopupTimelineLineHeight();

    // Use responsive calculation with better accuracy
    const isMobile = window.innerWidth < 768;
    const spacing = isMobile ? 16 : 20;
    const iconSize = isMobile ? 24 : 32;
    const textHeight = isMobile ? 75 : 85;
    // Account for first icon center position (approximately iconSize/2 + padding)
    const firstIconCenterOffset = isMobile ? 12 : 16; // Approximate first icon center from top
    const gapPerItem = spacing + iconSize + textHeight;
    // Line starts at 28px, so we need to calculate from first icon center to last completed icon center
    // Then subtract the offset from line start to first icon center
    const progressHeight =
      (completed - 1) * gapPerItem + firstIconCenterOffset - 28;
    return `${Math.max(0, progressHeight)}px`;
  }

  // Measure timeline heights after view is rendered
  measurePopupTimelineHeights(): void {
    if (
      !this.popupTimelineItems?.nativeElement ||
      !this.popupTimelineContainer?.nativeElement ||
      !this.showOrderUpdatesPopup
    ) {
      this.isMeasuringHeights = false;
      return;
    }

    if (this.isMeasuringHeights) return;
    this.isMeasuringHeights = true;

    // Use requestAnimationFrame for immediate measurement
    requestAnimationFrame(() => {
      const timelineContainer = this.popupTimelineContainer?.nativeElement;
      const timelineItems = this.popupTimelineItems?.nativeElement;

      if (!timelineContainer || !timelineItems || !this.showOrderUpdatesPopup) {
        this.isMeasuringHeights = false;
        return;
      }

      const items = timelineItems.querySelectorAll('.timeline-item');
      if (items.length <= 1) {
        this.popupTimelineLineHeight = '0px';
        this.popupTimelineProgressHeight = '0px';
        this.isMeasuringHeights = false;
        this.cdr.detectChanges();
        return;
      }

      // Get the timeline container's position for relative calculations
      const containerRect = timelineContainer.getBoundingClientRect();

      // Get first and last item icon positions
      const firstItem = items[0] as HTMLElement;
      const lastItem = items[items.length - 1] as HTMLElement;

      // Find the icon container (div with rounded-full class)
      const firstIconContainer = firstItem.querySelector(
        'div.rounded-full',
      ) as HTMLElement;
      const lastIconContainer = lastItem.querySelector(
        'div.rounded-full',
      ) as HTMLElement;

      if (firstIconContainer && lastIconContainer) {
        // Get positions relative to the timeline container
        const firstIconRect = firstIconContainer.getBoundingClientRect();
        const lastIconRect = lastIconContainer.getBoundingClientRect();

        // Calculate center positions relative to container
        const firstIconCenter =
          firstIconRect.top - containerRect.top + firstIconRect.height / 2;
        const lastIconCenter =
          lastIconRect.top - containerRect.top + lastIconRect.height / 2;

        // Line is positioned at top-7 (28px from top of container)
        const lineStartOffset = 28; // top-7 = 28px

        // Line should extend from line start to last icon center
        const lineHeight = lastIconCenter - lineStartOffset;
        this.popupTimelineLineHeight = `${Math.max(0, lineHeight)}px`;

        // Calculate progress height based on completed statuses
        const completed = this.popupStatusList.filter(
          (s: OrderStatus) => s.isCompleted,
        ).length;
        if (completed > 0 && completed <= items.length) {
          // Find the last completed item
          let lastCompletedIndex = -1;
          for (let i = items.length - 1; i >= 0; i--) {
            if (this.popupStatusList[i]?.isCompleted) {
              lastCompletedIndex = i;
              break;
            }
          }

          if (lastCompletedIndex >= 0) {
            const lastCompletedItem = items[lastCompletedIndex] as HTMLElement;
            const lastCompletedIconContainer = lastCompletedItem.querySelector(
              'div.rounded-full',
            ) as HTMLElement;
            if (lastCompletedIconContainer) {
              const lastCompletedIconRect =
                lastCompletedIconContainer.getBoundingClientRect();
              const lastCompletedIconCenter =
                lastCompletedIconRect.top -
                containerRect.top +
                lastCompletedIconRect.height / 2;
              // Progress line should extend from line start to last completed icon center
              const progressHeight = lastCompletedIconCenter - lineStartOffset;
              this.popupTimelineProgressHeight = `${Math.max(0, progressHeight)}px`;
            } else {
              this.popupTimelineProgressHeight = this.popupTimelineLineHeight;
            }
          } else {
            this.popupTimelineProgressHeight = '0px';
          }
        } else if (completed === 0) {
          this.popupTimelineProgressHeight = '0px';
        } else {
          this.popupTimelineProgressHeight = this.popupTimelineLineHeight;
        }
      } else {
        // Fallback to calculated height if measurement fails - use more accurate calculation
        this.calculateFallbackHeights();
      }

      this.isMeasuringHeights = false;
      this.cdr.detectChanges();
    });
  }

  // Fallback calculation when measurement fails
  calculateFallbackHeights(): void {
    const statusCount = this.popupStatusList?.length || 0;
    if (statusCount <= 1) {
      this.popupTimelineLineHeight = '0px';
      this.popupTimelineProgressHeight = '0px';
      return;
    }

    // More accurate calculation based on actual CSS values
    // space-y-4 = 16px (mobile), space-y-5 = 20px (desktop)
    // Icon: 24px (mobile w-6 h-6), 32px (desktop w-8 h-8)
    // Text content: varies but typically 70-90px per item
    // Check if mobile or desktop using window width
    const isMobile = window.innerWidth < 768; // md breakpoint
    const spacing = isMobile ? 16 : 20; // space-y-4 or space-y-5
    const iconSize = isMobile ? 24 : 32; // w-6 h-6 or w-8 h-8
    const textHeight = isMobile ? 75 : 85; // Approximate text content height

    // Distance between icon centers = spacing + iconSize/2 + textHeight + iconSize/2
    // Simplified: spacing + iconSize + textHeight
    const gapPerItem = spacing + iconSize + textHeight;

    // Line starts at top-7 (28px) and should reach center of last icon
    // First icon center is approximately at: iconSize/2 + some padding
    // For simplicity, we calculate: (count - 1) * gapPerItem + adjustment
    const totalHeight = (statusCount - 1) * gapPerItem;
    this.popupTimelineLineHeight = `${totalHeight}px`;

    // Calculate progress height
    const completed = this.popupStatusList.filter(
      (s: OrderStatus) => s.isCompleted,
    ).length;
    if (completed > 0 && completed < statusCount) {
      const progressHeight = (completed - 1) * gapPerItem;
      this.popupTimelineProgressHeight = `${progressHeight}px`;
    } else if (completed >= statusCount) {
      this.popupTimelineProgressHeight = this.popupTimelineLineHeight;
    } else {
      this.popupTimelineProgressHeight = '0px';
    }
  }

  // Returns the height of the grey timeline line based on number of status icons
  getTimelineLineHeight(): string {
    const statusCount = this.orderDetail?.status?.length || 0;
    if (statusCount <= 1) return '0px';
    // Calculate gap based on spacing between items (space-y-4 md:space-y-5 = 16px/20px)
    // Plus the icon size and padding, approximately 80px per item
    const gapPerItem =
      this.orderDetail?.isStorePickup ||
        this.orderDetail?.invoice_status === 'Return Requested' ||
        this.orderDetail?.invoice_status === 'Refund Credited'
        ? 75
        : 80; // Approximate spacing between status items including icon and padding
    // Line should extend from center of first icon to center of last icon, so (count - 1) gaps
    return `${(statusCount - 1) * gapPerItem}px`;
  }
  // Returns the height of the blue progress line for the vertical timeline
  getTimelineProgressHeight(): string {
    const statuses = this.orderDetail?.status || [];
    const completed = statuses.filter((s: any) => s.isCompleted).length;
    const total = statuses.length;
    if (total <= 1) return '0px';
    // Use the same gap calculation as getTimelineLineHeight
    const gapPerItem =
      this.orderDetail?.isStorePickup ||
        this.orderDetail?.invoice_status === 'Return Requested' ||
        this.orderDetail?.invoice_status === 'refund credited'
        ? 75
        : 80;
    // Full height should extend from first to last icon center, so (total - 1) gaps
    const fullHeight = (total - 1) * gapPerItem;

    console.log(completed, total, fullHeight);
    // Calculate height based on completed statuses
    // If 2 out of 3 are completed, the line should extend to the second status
    const percent =
      completed > 0 ? Math.max(0, (completed - 1) / (total - 1)) : 0;
    return `${percent * fullHeight}px`;
  }
  @Input() orderId?: string;
  ratingSubmitted = false;
  userData: any;
  orderStatuslistDetails: any;
  seeAllUpdatesStatusDetails: any;
  popupStatusList: OrderStatus[] = [];
  // Product cancellation popup properties
  showCancelPopup = false;
  cancelStep: 'select' | 'details' = 'select';
  canProceed = false;
  currentProductIndex = 0;
  selectedProducts: OrderItem[] = [];
  currentProduct: OrderItem | null = null;
  isLastProduct = false;

  // Return popup properties
  showReturnPopup = false;
  returnStep: 'select' | 'details' = 'select';
  canProceedReturn = false;
  currentReturnProductIndex = 0;
  selectedReturnProducts: OrderItem[] = [];
  currentReturnProduct: OrderItem | null = null;
  isLastReturnProduct = false;

  // Getter/setter for current return product comment to support two-way binding
  get currentReturnComment(): string {
    return this.currentReturnProduct?.returnComment || '';
  }
  set currentReturnComment(value: string) {
    if (this.currentReturnProduct) {
      this.currentReturnProduct.returnComment = value;
    }
  }

  // Order updates popup properties
  showOrderUpdatesPopup = false;
  popupTimelineLineHeight = '0px';
  popupTimelineProgressHeight = '0px';

  // Rating popup properties
  showRatingPopup = false;
  selectedRating: number = 0; // Default to 0 (no selection - gray)
  selectedChips: string[] = [];
  reviewComment: string = '';

  // Dynamic feedback chips based on rating
  feedbackChips = {
    poor: {
      question: "What didn't go well with the order?",
      chips: [
        'Wrong Part',
        'Bad Quality',
        'Late Delivery',
        'Website Crash',
        'Confusing Website',
      ],
    },
    good: {
      question: 'What can be improved?',
      chips: [
        'Average Quality',
        'Weak Packaging',
        'Delivery Delay',
        'Website Lag',
        'Slow Support',
      ],
    },
    excellent: {
      question: 'What went really well?',
      chips: [
        'Searching Product',
        'Perfect Fit',
        'Fast Delivery',
        'Smooth Website',
        'Easy Process',
      ],
    },
  };

  // Cancellation reasons
  cancellationReasons: CancellationReason[] = [
    { value: 'better_price', label: 'Found a better price elsewhere' },
    { value: 'ordered_mistake', label: 'Ordered by mistake' },
    { value: 'delivery_time', label: 'Delivery time is too long' },
    { value: 'changed_mind', label: 'Changed my mind' },
    { value: 'other', label: 'Other reason' },
  ];

  // Return reasons with refund percentages
  returnReasons: ReturnReason[] = [
    {
      value: 'Damage Parcel & Part Received',
      label: 'Damage Parcel & Part Received',
      refundPercentage: 100,
      message: 'Full refund will be processed after verification',
    },
    {
      value: 'Different part received than ordered',
      label: 'Different part received than ordered',
      refundPercentage: 100,
      message: 'Full refund will be processed after verification',
    },
    {
      value: 'Damage part received',
      label: 'Damage part received',
      refundPercentage: 100,
      message: 'Full refund will be processed after verification',
    },
    {
      value: 'Parts does not fit on intended vehicle',
      label: 'Parts does not fit on intended vehicle',
      refundPercentage: 90,
      message: '90% refund will be processed (10% restocking fee)',
    },
    {
      value: 'Parts not working when fitted on intended vehicl',
      label: 'Parts not working when fitted on intended vehicle',
      refundPercentage: 95,
      message: '95% refund will be processed (5% testing fee)',
    },
  ];

  breadcrumbs = [
    // { label: 'Home', url: '/home', active: false },
    {
      label: 'My Account',
      url: '/user-profile?section=orders',
      active: false,
    },
    {
      label: 'Order & Returns',
      url: '/user-profile?section=orders',
      active: true,
    },
  ];

  isLock: any;
  orderDetail: any;
  chekcInvoice_umnber: any;
  apiData: any;
  deleveryType: boolean = false;
  orderancel: boolean = false;
  sub: Subscription = new Subscription();
  formBuilder: any;
  ccavenue: any;
  returnResponse: any;
  isBookingFlow: boolean = false;
  pendingPaymentInvoices: PendingPaymentInvoice[] = [];
  expandedPendingInvoices: { [key: string]: boolean } = {};
  document_type: any;
  isDocumentLoading: boolean = false;
  constructor(
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    private loaderService: LoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public paymentService: PaymentService,
    private cdr: ChangeDetectorRef,
    private cartService: CartService,
    private notificationService: NotificationService,
    private documentService: DocumentService,
  ) {
    this.formBuilder = this.fb;

    const navigation = this.router.getCurrentNavigation();
    const navState =
      navigation?.extras?.state ||
      (typeof window !== 'undefined' && window.history?.state?.['orderData']
        ? window.history.state
        : null);

    if (navState && navState['orderData']) {
      this.apiData = { ...navState['orderData'] };
      this.isBookingFlow = !!navState['isBookingFlow'];
      const fromOrderConfirmation =
        !!navState['fromOrderConfirmation'];
      if (fromOrderConfirmation) {
        this.isBookingFlow = true;
      }
      if (navState['isEnquiry']) {
        this.isBookingFlow = true;
        this.apiData.isEnquiry = true;
        this.apiData.document_type = 'ENQUIRY';
      }
      console.log('Received order data from navigation state:', this.apiData);
      this.loadOrderDetails();
    }
  }

  private resizeObserver?: ResizeObserver;

  ngOnInit(): void {
    // Scroll to top when component loads
    window.scrollTo(0, 0);
    // Add keyboard event listener for Escape key
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    // Add window resize listener
    window.addEventListener('resize', this.onWindowResize.bind(this));
    try {
      const storedUserData = localStorage.getItem('userData1') || localStorage.getItem('userData');
      if (storedUserData) {
        this.userData = typeof storedUserData === 'string' ? JSON.parse(storedUserData) : storedUserData;
      }
    } catch (e) {
      this.userData = null;
    }
    this.loadUserDetails();

    if (!this.apiData) {
      const qp = this.route.snapshot.queryParams;
      const bNo = qp['booking_number'] || qp['enquiry_number'] || '';
      const oNo = qp['order_number'] || qp['order_id'] || '';
      const isEnq =
        qp['is_enquiry'] === 'true' ||
        bNo.toUpperCase().startsWith('ENQ-') ||
        oNo.toUpperCase().startsWith('ENQ-');
      if (bNo || oNo) {
        this.apiData = {
          booking_number: bNo || oNo,
          order_number: oNo || bNo,
          document_type: isEnq ? 'ENQUIRY' : (qp['document_type'] || ''),
          isEnquiry: isEnq,
        };
        this.isBookingFlow = isEnq || !!bNo;
        this.loadOrderDetails();
      }
    }
    // getorderstatusDetails() will be called after orderDetail is created in getorderlist()
    // this.seeAllUpdatesStatus();
  }

  onWindowResize(): void {
    // Recalculate timeline heights on window resize
    if (this.showOrderUpdatesPopup && this.popupStatusList.length > 0) {
      this.scheduleMeasurement();
    }
  }

  // Toggle order updates popup and fetch status data when opening
  toggleOrderUpdatesPopup(): void {
    if (!this.showOrderUpdatesPopup) {
      // Reset heights when opening
      this.popupTimelineLineHeight = '0px';
      this.popupTimelineProgressHeight = '0px';
      this.lastStatusListLength = 0;
      // Opening popup - fetch all statuses from API
      this.seeAllUpdatesStatus();
    }
    this.showOrderUpdatesPopup = !this.showOrderUpdatesPopup;
    // Measure heights after popup is shown - use requestAnimationFrame for immediate measurement
    if (this.showOrderUpdatesPopup) {
      this.scheduleMeasurement();
    } else {
      this.lastStatusListLength = 0;
    }
  }

  // Toggle rating popup
  toggleRatingPopup(): void {
    this.showRatingPopup = !this.showRatingPopup;
    if (!this.showRatingPopup) {
      this.resetRatingData();
    }
  }

  // Select rating
  selectRating(rating: number): void {
    this.selectedRating = rating;
    // Clear selected chips when rating changes
    this.selectedChips = [];
  }

  // Get current feedback data based on selected rating
  getCurrentFeedbackData(): { question: string; chips: string[] } {
    switch (this.selectedRating) {
      case 1:
        return this.feedbackChips.poor;
      case 2:
        return this.feedbackChips.good;
      case 3:
        return this.feedbackChips.excellent;
      default:
        return this.feedbackChips.excellent;
    }
  }

  // Toggle chip selection
  toggleChip(chip: string): void {
    const index = this.selectedChips.indexOf(chip);
    if (index > -1) {
      this.selectedChips.splice(index, 1);
    } else {
      this.selectedChips.push(chip);
    }
  }

  // Check if chip is selected
  isChipSelected(chip: string): boolean {
    return this.selectedChips.includes(chip);
  }

  // Submit rating
  submitRating(): void {
    const payload = {
      // order_number: this.apiData.order_number,
      invoice_number: this.apiData.invoice_number,
      invoice_ratings:
        this.selectedRating == 1
          ? 'poor'
          : this.selectedRating == 2
            ? 'average'
            : 'excellent',
      chips: this.selectedChips,
      review: this.reviewComment,
    };

    this.sub.add(
      this.apiService.ratingOrder(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            // this.ratingSubmitted = true;
            // this.showRatingPopup = false;
            this.showRatingPopup = false;
            // this.toggleRatingPopup;
            this.getorderlist();
            this.notificationService.success('Rating submitted successfully!');
          } else {
            // this.notificationService.error(
            //   'Failed to submit rating. Please try again.',
            // );
          }
        },
        error: (err) => {
          console.error('Error submitting rating:', err);
          // this.notificationService.error(
          //   'Something went wrong. Please try again later.',
          // );
        },
      }),
    );
  }

  // Reset rating data
  resetRatingData(): void {
    this.selectedRating = 0;
    this.selectedChips = [];
    this.reviewComment = '';
  }

  // Handle keyboard events
  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.showOrderUpdatesPopup) {
        this.showOrderUpdatesPopup = false;
      }
      if (this.showRatingPopup) {
        this.showRatingPopup = false;
      }
    }
  }

  // TrackBy function for ngFor performance
  trackByStatus(index: number, status: OrderStatus): string {
    return status.stage;
  }

  // Cleanup event listeners
  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private loadOrderDetails(): void {
    if (!this.apiData) {
      return;
    }
    if (this.shouldFetchBookingDetail()) {
      this.getBookingDetail();
      return;
    }
    this.getorderlist();
  }

  private shouldFetchBookingDetail(): boolean {
    const documentType = String(this.apiData?.document_type || '').toUpperCase();
    const invoiceNumber = String(this.apiData?.invoice_number || '').toUpperCase();
    const bookingNumber = String(
      this.apiData?.booking_number || this.apiData?.booking_no || '',
    ).toUpperCase();
    const orderNumber = String(this.apiData?.order_number || '').toUpperCase();
    const hasBookingNumber = !!(
      this.apiData?.booking_number ||
      this.apiData?.booking_no
    );

    return (
      this.isBookingFlow ||
      hasBookingNumber ||
      documentType === 'BOOKING' ||
      documentType === 'PROFORMA' ||
      documentType === 'ENQUIRY' ||
      !!this.apiData?.isEnquiry ||
      orderNumber.startsWith('ENQ-') ||
      invoiceNumber.startsWith('PI-') ||
      bookingNumber.startsWith('ENQ-') ||
      invoiceNumber.startsWith('ENQ-')
    );
  }

  private getBookingDetail(): void {
    const bookingNumber = String(
      this.apiData?.booking_number || this.apiData?.booking_no || '',
    ).trim();
    const orderNumber = String(this.apiData?.order_number || '').trim();
    const documentType = String(this.apiData?.document_type || '').trim();
    const isEnquiry =
      !!this.apiData?.isEnquiry ||
      documentType.toUpperCase() === 'ENQUIRY' ||
      bookingNumber.toUpperCase().startsWith('ENQ-') ||
      orderNumber.toUpperCase().startsWith('ENQ-') ||
      String(this.apiData?.invoice_number || '').toUpperCase().startsWith('ENQ-');

    if (!orderNumber && !bookingNumber) {
      this.getorderlist();
      return;
    }

    this.loaderService.show();
    let payload: any;
    if (isEnquiry) {
      payload = {
        action_type: 'detail',
        booking_number: bookingNumber || orderNumber,
      };
    } else {
      payload = {
        action_type: 'detail',
      };
      if (orderNumber) {
        payload.order_number = orderNumber;
      }
      if (bookingNumber) {
        payload.booking_number = bookingNumber;
      }
      if (documentType) {
        payload.document_type = documentType;
      }
    }

    this.sub.add(
      this.apiService.getBookingData(payload).subscribe({
        next: (res: any) => {
          let detailData: any = res?.data;
          if (Array.isArray(detailData?.booking_list) && detailData.booking_list.length > 0) {
            detailData = {
              ...detailData,
              ...detailData.booking_list[0],
            };
          } else if (Array.isArray(detailData?.enquiry_list) && detailData.enquiry_list.length > 0) {
            detailData = {
              ...detailData,
              ...detailData.enquiry_list[0],
            };
          } else if (Array.isArray(detailData) && detailData.length > 0) {
            detailData = detailData[0];
          }

          if (res?.success && detailData) {
            this.apiData = {
              ...this.apiData,
              ...detailData,
              order_number: detailData?.order_number || (orderNumber && orderNumber !== bookingNumber ? orderNumber : ''),
              booking_number: detailData?.booking_number || bookingNumber || '',
              generated_pin: detailData?.generated_pin ?? this.apiData?.generated_pin ?? '',
              invoice_status: this.normalizeBookingStatus(
                detailData?.order_status ||
                detailData?.booking_status ||
                this.apiData?.invoice_status ||
                '',
              ),
              document_type:
                detailData?.document_type || this.apiData?.document_type || (isEnquiry ? 'ENQUIRY' : ''),
            };
            this.document_type =
              detailData?.document_type === 'PROFORMA'
                ? 'Proforma'
                : detailData?.document_type === 'BOOKING'
                  ? 'Booking'
                  : detailData?.document_type === 'ENQUIRY' || isEnquiry
                    ? 'Enquiry'
                    : '';
            this.orderDetail = this.mapBookingDetailToOrderDetail(detailData);
            this.deleveryType = this.orderDetail?.isStorePickup || false;
            this.loadPendingPaymentInvoices();
            this.orderInvoiceprice();
            this.loaderService.hide();
            this.cdr.detectChanges();
            return;
          }

          this.loaderService.hide();
          this.notificationService.error(
            res?.data?.msg || 'Failed to load details.',
          );
        },
        error: (err: any) => {
          this.loaderService.hide();
          this.notificationService.error(
            err?.error?.data?.msg || 'Failed to load details.',
          );
        },
      }),
    );
  }

  // ---------- Helpers ----------
  getUserBillingAddress(): Address {
    const rawBilling =
      (hasAddress(this.userData?.billing_address)
        ? this.userData?.billing_address
        : null) ||
      (hasAddress(this.userData?.shipping_address)
        ? this.userData?.shipping_address
        : null);

    const userName =
      `${this.userData?.first_name || ''} ${this.userData?.last_name || ''}`.trim() ||
      this.userData?.account_name ||
      '';

    const name =
      rawBilling?.company_name ||
      rawBilling?.receiver_name ||
      userName ||
      '';

    const pincode = String(rawBilling?.pincode || '').trim();
    const formatted = formatAddress(rawBilling);
    const gst =
      rawBilling?.gstin_number ||
      rawBilling?.gst_number ||
      undefined;
    const type = (rawBilling?.address_type as 'Home' | 'Work' | 'Other') || 'Work';

    return {
      name,
      pincode,
      address: formatted,
      gst,
      type,
    };
  }

  getUserShippingAddress(): Address {
    const rawShipping =
      (hasAddress(this.userData?.shipping_address)
        ? this.userData?.shipping_address
        : null) ||
      (hasAddress(this.userData?.billing_address)
        ? this.userData?.billing_address
        : null);

    const userName =
      `${this.userData?.first_name || ''} ${this.userData?.last_name || ''}`.trim() ||
      this.userData?.account_name ||
      '';

    const name =
      rawShipping?.receiver_name ||
      rawShipping?.company_name ||
      userName ||
      '';

    const pincode = String(rawShipping?.pincode || '').trim();
    const formatted = formatAddress(rawShipping);
    const type = (rawShipping?.address_type as 'Home' | 'Work' | 'Other') || 'Home';

    return {
      name,
      pincode,
      address: formatted,
      type,
    };
  }

  private applyUserDetailAddresses(): void {
    if (!this.orderDetail) {
      return;
    }
    const userBilling = this.getUserBillingAddress();
    if (!this.orderDetail.billingAddress?.address && userBilling?.address) {
      this.orderDetail.billingAddress = {
        ...(this.orderDetail.billingAddress || {}),
        ...userBilling,
        name: this.orderDetail.billingAddress?.name || userBilling.name,
      };
    } else if (!this.orderDetail.billingAddress?.name && userBilling?.name) {
      this.orderDetail.billingAddress = {
        ...(this.orderDetail.billingAddress || {}),
        name: userBilling.name,
      };
    }

    if (!this.orderDetail.isStorePickup && !this.deleveryType) {
      const userShipping = this.getUserShippingAddress();
      if (!this.orderDetail.shippingAddress?.address && userShipping?.address) {
        this.orderDetail.shippingAddress = {
          ...(this.orderDetail.shippingAddress || {}),
          ...userShipping,
          name: this.orderDetail.shippingAddress?.name || userShipping.name,
        };
      } else if (!this.orderDetail.shippingAddress?.name && userShipping?.name) {
        this.orderDetail.shippingAddress = {
          ...(this.orderDetail.shippingAddress || {}),
          name: userShipping.name,
        };
      }
    }

    if (!this.orderDetail.sellerAddress?.address && !this.orderDetail.sellerAddress?.name) {
      const sellerAddr = this.extractSellerAddress(this.apiData, this.orderDetail.items);
      if (sellerAddr.address || sellerAddr.name) {
        this.orderDetail.sellerAddress = sellerAddr;
        this.orderDetail.storeAddress = sellerAddr;
      }
    }
  }

  loadUserDetails(): void {
    this.sub.add(
      this.apiService.getUserDetails({}).subscribe({
        next: (res: any) => {
          if (res?.data) {
            this.userData = res.data;
            try {
              localStorage.setItem('userData1', JSON.stringify(res.data));
            } catch (e) { }
            this.applyUserDetailAddresses();
            this.cdr.detectChanges();
          }
        },
        error: (err: any) => {
          console.warn('Failed to load user details in order details:', err);
        },
      }),
    );
  }

  private round2(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  // Backend sends "Payment Initialized" for a booking whose payment is not done yet.
  // The template only knows "Payment Pending", so normalise it.
  private normalizeBookingStatus(status: string): string {
    const s = (status || '').trim().toLowerCase();
    if (
      ['payment initialized', 'payment initiated', 'payment pending'].includes(s)
    ) {
      return 'Payment Pending';
    }
    return status || 'Order Placed';
  }

  private resolveIsStorePickup(data: any, lineItems?: any[]): boolean {
    if (
      data?.is_store_pickup === true ||
      data?.isStorePickup === true ||
      this.apiData?.is_store_pickup === true ||
      this.apiData?.isStorePickup === true
    ) {
      return true;
    }

    const items =
      lineItems ||
      (Array.isArray(data?.order_line_item) ? data.order_line_item : []) ||
      (Array.isArray(data?.line_items) ? data.line_items : []) ||
      (Array.isArray(data?.parts) ? data.parts : []) ||
      (Array.isArray(data?.items) ? data.items : []) ||
      (Array.isArray(data?.part_details) ? data.part_details : []) ||
      (Array.isArray(data?.invoice_items) ? data.invoice_items : []);

    const candidates: any[] = [
      data?.delivery_type,
      data?.delivery_option,
      data?.deliveryType,
      data?.deliveryOption,
      data?.shipping_method,
      data?.order_delivery_type,
      this.apiData?.delivery_type,
      this.apiData?.deliveryType,
      this.apiData?.delivery_option,
    ];

    if (Array.isArray(items)) {
      for (const item of items) {
        if (item?.delivery_type) candidates.push(item.delivery_type);
        if (item?.delivery_option) candidates.push(item.delivery_option);
        if (item?.deliveryType) candidates.push(item.deliveryType);
      }
    }

    for (const val of candidates) {
      if (typeof val === 'string' && val.trim()) {
        const normalized = val.toLowerCase().replace(/[-_]/g, ' ').trim();
        if (
          normalized === 'store pickup' ||
          normalized === 'pickup' ||
          normalized === 'self pickup' ||
          normalized.includes('pickup') ||
          normalized.includes('store')
        ) {
          return true;
        }
        if (normalized === 'home delivery' || normalized === 'home') {
          return false;
        }
      }
    }

    if (data?.generated_pin || this.apiData?.generated_pin) {
      return true;
    }

    const addresses = Array.isArray(data?.order_address) ? data.order_address : [];
    const hasShippingAddr =
      addresses.some((a: any) => a?.order_address_type === 'shipping_address') ||
      Boolean(
        data?.shipping_address?.address_1 ||
        data?.shipping_address?.address ||
        data?.shipping_address?.receiver_name,
      );

    const hasSeller = Boolean(
      data?.seller_detail ||
      data?.seller_details ||
      data?.seller ||
      this.apiData?.seller_detail ||
      (Array.isArray(items) && items.some((i: any) => i?.seller_detail || i?.seller)),
    );

    if (!hasShippingAddr && hasSeller) {
      return true;
    }

    return false;
  }

  getSellerAddress(): Address {
    if (this.orderDetail?.sellerAddress?.address || this.orderDetail?.sellerAddress?.name) {
      return this.orderDetail.sellerAddress;
    }
    if (this.orderDetail?.storeAddress?.address || this.orderDetail?.storeAddress?.name) {
      return this.orderDetail.storeAddress;
    }
    return this.extractSellerAddress(this.apiData, this.orderDetail?.items);
  }

  private extractSellerAddress(data: any, lineItems?: any[]): Address {
    const items =
      lineItems ||
      (Array.isArray(data?.order_line_item) ? data.order_line_item : []) ||
      (Array.isArray(data?.line_items) ? data.line_items : []) ||
      (Array.isArray(data?.parts) ? data.parts : []) ||
      (Array.isArray(data?.items) ? data.items : []) ||
      (Array.isArray(data?.part_details) ? data.part_details : []) ||
      (Array.isArray(data?.invoice_items) ? data.invoice_items : []);

    const seller =
      data?.seller_detail ||
      data?.seller_details ||
      data?.seller ||
      this.apiData?.seller_detail ||
      this.apiData?.seller_details ||
      this.apiData?.seller ||
      items?.find((i: any) => i?.seller_detail || i?.seller)?.seller_detail ||
      items?.find((i: any) => i?.seller_detail || i?.seller)?.seller;

    const addresses = Array.isArray(data?.order_address)
      ? data.order_address
      : Array.isArray(this.apiData?.order_address)
        ? this.apiData.order_address
        : [];
    const storeAddrObj = addresses.find((a: any) => a?.order_address_type === 'store_address');

    const sellerName =
      seller?.seller_name ||
      seller?.branch_name ||
      seller?.name ||
      seller?.company_name ||
      storeAddrObj?.receiver_name ||
      storeAddrObj?.company_name ||
      data?.seller_name ||
      this.apiData?.seller_name ||
      items?.find((i: any) => i?.seller_name)?.seller_name ||
      '';

    let sellerPincode =
      seller?.pincode ||
      seller?.pin_code ||
      seller?.postal_code ||
      storeAddrObj?.pincode ||
      data?.seller_pincode ||
      data?.seller_pin ||
      this.apiData?.seller_pincode ||
      '';

    let sellerAddressStr =
      seller?.address ||
      [
        seller?.address_1,
        seller?.address_2,
        seller?.address_3,
        seller?.city,
        seller?.state,
      ].filter(Boolean).join(', ') ||
      (storeAddrObj
        ? [
            storeAddrObj.address_1,
            storeAddrObj.address_2,
            storeAddrObj.address_3,
            storeAddrObj.city,
            storeAddrObj.state,
          ].filter(Boolean).join(', ') || storeAddrObj.address
        : '') ||
      data?.seller_address ||
      data?.store_address ||
      this.apiData?.seller_address ||
      this.apiData?.store_address ||
      '';

    return {
      name: sellerName,
      pincode: sellerPincode ? String(sellerPincode) : '',
      address: sellerAddressStr || (sellerName ? 'Store address will be shared by seller' : ''),
      type: 'Other',
    };
  }

  private mapBookingDetailToOrderDetail(bookingData: any): OrderDetail {
    const lineItems = Array.isArray(bookingData?.order_line_item) && bookingData.order_line_item.length > 0
      ? bookingData.order_line_item
      : Array.isArray(bookingData?.parts) && bookingData.parts.length > 0
        ? bookingData.parts
        : Array.isArray(bookingData?.items) && bookingData.items.length > 0
          ? bookingData.items
          : Array.isArray(bookingData?.line_items) && bookingData.line_items.length > 0
            ? bookingData.line_items
            : Array.isArray(bookingData?.part_details) && bookingData.part_details.length > 0
              ? bookingData.part_details
              : Array.isArray(bookingData?.invoice_items) && bookingData.invoice_items.length > 0
                ? bookingData.invoice_items
                : [];
    const status = this.normalizeBookingStatus(
      bookingData?.order_status || bookingData?.booking_status,
    );
    const isPaymentPending = status === 'Payment Pending';
    const isEnquiry =
      String(bookingData?.document_type || this.document_type || this.apiData?.document_type || '').toUpperCase() === 'ENQUIRY' ||
      String(bookingData?.booking_number || '').toUpperCase().startsWith('ENQ-') ||
      String(bookingData?.order_number || '').toUpperCase().startsWith('ENQ-') ||
      !!this.apiData?.isEnquiry;
    const bookingDate = this.toDatePipeValue(bookingData?.order_date);
    const seller =
      bookingData?.seller_detail ||
      bookingData?.seller_details ||
      bookingData?.seller ||
      this.apiData?.seller_detail ||
      this.apiData?.seller_details ||
      this.apiData?.seller ||
      lineItems?.find((i: any) => i?.seller_detail || i?.seller)?.seller_detail ||
      lineItems?.find((i: any) => i?.seller_detail || i?.seller)?.seller;

    const sellerAddress: Address = this.extractSellerAddress(bookingData, lineItems);

    const customerName =
      bookingData?.customer_name ||
      `${this.userData?.first_name || ''} ${this.userData?.last_name || ''}`.trim();

    const items: OrderItem[] = lineItems.map((item: any) => ({
      id: String(item?.order_line_id || item?.inventory_id || item?.part_number || item?.part_no || ''),
      name: item?.part_name || item?.part_desc || '',
      price: this.extractPriceFromString(item?.total_amount ?? item?.item_amount ?? item?.price ?? 0),
      image: item?.part_image || item?.image_url || 'assets/default.webp',
      engine: '',
      seller: {
        seller_code: item?.seller_code || bookingData?.seller_code || seller?.seller_code || '',
        seller_name: item?.seller_name || seller?.seller_name || sellerAddress?.name || '',
        pincode: sellerAddress?.pincode || '',
        address: sellerAddress?.address || '',
      },
      grade: item?.quality || '',
      brand: item?.make || '',
      model: item?.model || '',
      year: '',
      invoiceNumber: bookingData?.booking_number || bookingData?.order_number || '',
      quantity: String(item?.quantity ?? '1'),
      total_quantity: String(item?.quantity ?? '1'),
      part_no: item?.part_number || item?.part_no || '',
      available_quantity: Number(item?.quantity ?? 1),
      integration_id: item?.integration_id || '',
      item_id: item?.order_line_id || '',
      booking_amount: item?.booking_amount || '',
    }));

    const sumOf = (field: string) =>
      this.round2(
        lineItems.reduce(
          (sum: number, item: any) =>
            sum + this.extractPriceFromString(item?.[field]),
          0,
        ),
      );
    const totalAmount = sumOf('total_amount');
    const taxAmount = sumOf('tax_amount');
    const tcsAmount = sumOf('tcs_amount');
    const pendingAmount = sumOf('pending_amount');
    const taxableAmount = this.round2(totalAmount - taxAmount - tcsAmount);
    const bookingAmount =
      this.extractPriceFromString(bookingData?.booking_amount) ||
      sumOf('booking_amount');

    const storeAddress: Address = sellerAddress;

    const addresses = Array.isArray(bookingData?.order_address) ? bookingData.order_address : [];
    const bAddress = addresses.find((a: any) => a?.order_address_type === 'billing_address') || bookingData?.billing_address;
    const userBilling = this.getUserBillingAddress();
    const mappedBookingBilling: Address = (bAddress?.address_1 || bAddress?.company_name || bAddress?.receiver_name)
      ? {
        name: bAddress.company_name || bAddress.receiver_name || customerName,
        pincode: bAddress.pincode || '',
        address: [bAddress.address_1, bAddress.address_2, bAddress.address_3, bAddress.city, bAddress.state].filter(Boolean).join(', ') || bAddress.address || '',
        gst: bAddress.gstin_number || bAddress.gst || undefined,
        type: (bAddress.address_type as 'Home' | 'Work' | 'Other') || 'Work',
      }
      : (userBilling.address || userBilling.name)
        ? userBilling
        : { name: customerName, pincode: '', address: '' };

    const isStorePickup = this.resolveIsStorePickup(bookingData, lineItems);

    const sAddress = addresses.find((a: any) => a?.order_address_type === 'shipping_address') || bookingData?.shipping_address;
    const userShipping = this.getUserShippingAddress();
    const mappedBookingShipping: Address = isStorePickup
      ? storeAddress
      : (sAddress?.address_1 || sAddress?.company_name || sAddress?.receiver_name)
        ? {
          name: sAddress.receiver_name || sAddress.company_name || customerName,
          pincode: sAddress.pincode || '',
          address: [sAddress.address_1, sAddress.address_2, sAddress.address_3, sAddress.city, sAddress.state].filter(Boolean).join(', ') || sAddress.address || '',
          gst: sAddress.gstin_number || undefined,
          type: (sAddress.address_type as 'Home' | 'Work' | 'Other') || 'Home',
        }
        : (userShipping.address || userShipping.name)
          ? userShipping
          : storeAddress;

    return {
      id: bookingData?.order_number || bookingData?.booking_number || '',
      invoiceNo: bookingData?.booking_number || bookingData?.order_number || '',
      orderNo: bookingData?.order_number || bookingData?.booking_number || '',
      estimatedDelivery:
        this.toDatePipeValue(
          bookingData?.delivery_date ||
          bookingData?.tracking_dt ||
          bookingData?.expected_delivery_date ||
          bookingData?.estimated_delivery_date,
        ) || this.getFutureTwoDaysDate(),
      bookingDate,
      isBooking: true,
      status: (isPaymentPending || isEnquiry)
        ? []
        : status === 'Order Cancelled'
          ? [
            {
              stage: 'Order Cancelled',
              isCompleted: true,
              date: bookingDate,
              description: status,
              timestamp: bookingDate,
              icon: 'order-cancelled',
            },
          ]
          : [
            {
              stage: 'Order Placed',
              isCompleted: true,
              date: bookingDate,
              description: 'Order Placed completed',
              timestamp: bookingDate,
              icon: 'order-placed',
            },
            {
              stage: 'Order Ready',
              isCompleted: ['order ready', 'picked up', 'delivered'].includes(status.toLowerCase()),
              date: ['order ready', 'picked up', 'delivered'].includes(status.toLowerCase()) ? bookingDate : '',
              description: ['order ready', 'picked up', 'delivered'].includes(status.toLowerCase()) ? 'Order Ready completed' : 'Order Ready pending',
              timestamp: ['order ready', 'picked up', 'delivered'].includes(status.toLowerCase()) ? bookingDate : '',
              icon: 'order-ready',
            },
            {
              stage: 'Picked Up',
              isCompleted: ['picked up', 'delivered'].includes(status.toLowerCase()),
              date: ['picked up', 'delivered'].includes(status.toLowerCase()) ? bookingDate : '',
              description: ['picked up', 'delivered'].includes(status.toLowerCase()) ? 'Picked Up completed' : 'Picked Up pending',
              timestamp: ['picked up', 'delivered'].includes(status.toLowerCase()) ? bookingDate : '',
              icon: 'pickup',
            },
          ],
      items,
      billingAddress: mappedBookingBilling,
      shippingAddress: mappedBookingShipping,
      sellerAddress: sellerAddress,
      storeAddress: sellerAddress,
      priceBreakdown: {
        subtotal: taxableAmount,
        discount: 0,
        platformCharges: 0,
        deliveryCharges: 0,
        gst: taxAmount,
        total: totalAmount,
        scrap_booking_amount: bookingAmount,
        base_price: taxableAmount,
        gst_amount: taxAmount,
        platform_charges: 0,
        delivery_charges: 0,
        order_total: totalAmount,
        tcs_amount: tcsAmount,
        pending_amount: pendingAmount,
      },
      canCancel: status !== 'Delivered' && bookingData?.document_type !== 'ENQUIRY',
      isStorePickup,
      isEnquiry,
      invoicePdfPath: bookingData?.booking_pdf_path || '',
      invoiceRating: 0,
      invoice_status: status,
      generated_pin: bookingData?.generated_pin ?? bookingData?.pin ?? bookingData?.pickup_pin ?? this.apiData?.generated_pin ?? '',
    };
  }

  private toDatePipeValue(value: any): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(
      typeof value === 'string' ? value.replace(' ', 'T') : value,
    );
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get futureTwoDaysDate(): string {
    return this.getFutureTwoDaysDate();
  }

  getFutureTwoDaysDate(): string {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onViewBookingClick(): void {
    if (!this.pendingPaymentInvoices?.length) {
      this.notificationService.error('Payment invoice is not available.');
      return;
    }

    this.payInvoice(this.pendingPaymentInvoices[0]);
  }

  payInvoice(invoice: PendingPaymentInvoice): void {
    if (!invoice) {
      this.notificationService.error('Payment invoice is not available.');
      return;
    }

    const orderForPayment = {
      id: this.orderDetail?.id || this.apiData?.order_number,
      payment_flow:
        this.orderDetail?.isBooking || this.isBookingFlow
          ? 'booking'
          : undefined,
      items: this.orderDetail?.items || [],
      invoice,
    };

    if (!orderForPayment.id) {
      this.notificationService.error('Order number not available.');
      return;
    }

    try {
      localStorage.setItem(
        'proformaPaymentContext',
        JSON.stringify({
          orderId: orderForPayment.id,
          invoiceId: invoice.invoice_id,
          bookingNumber: invoice.booking_number || this.orderDetail?.invoiceNo,
          paymentAmount: invoice.payment_amount,
          items: invoice.part_details || this.orderDetail?.items || [],
          orderDetail: this.orderDetail,
          invoice: invoice,
          timestamp: Date.now(),
        }),
      );
    } catch (_) { }

    this.initiatePayment(orderForPayment);
  }

  togglePendingPaymentCard(invoice: PendingPaymentInvoice): void {
    const key = this.getPendingPaymentCardKey(invoice);
    this.expandedPendingInvoices[key] = !this.expandedPendingInvoices[key];
  }

  isPendingPaymentCardExpanded(invoice: PendingPaymentInvoice): boolean {
    return !!this.expandedPendingInvoices[this.getPendingPaymentCardKey(invoice)];
  }

  getPendingPaymentItemsLabel(invoice: PendingPaymentInvoice): string {
    const count = Array.isArray(invoice?.part_details)
      ? invoice.part_details.length
      : 0;
    return `${count} item${count === 1 ? '' : 's'}`;
  }

  private getPendingPaymentCardKey(invoice: PendingPaymentInvoice): string {
    return String(invoice?.invoice_id || invoice?.booking_number || '0');
  }

  isEnquiry(): boolean {
    const docType = String(this.document_type || this.apiData?.document_type || '').toUpperCase();
    const invoiceNo = String(this.orderDetail?.invoiceNo || '').toUpperCase();
    const bookingNo = String(this.apiData?.booking_number || this.apiData?.booking_no || '').toUpperCase();
    const invoiceNumber = String(this.apiData?.invoice_number || '').toUpperCase();
    const orderNumber = String(this.apiData?.order_number || '').toUpperCase();

    return (
      !!this.apiData?.isEnquiry ||
      docType === 'ENQUIRY' ||
      invoiceNo.startsWith('ENQ-') ||
      bookingNo.startsWith('ENQ-') ||
      orderNumber.startsWith('ENQ-') ||
      invoiceNumber.startsWith('ENQ-')
    );
  }

  private loadPendingPaymentInvoices(): void {
    if (this.isEnquiry() || !this.shouldFetchBookingDetail()) {
      this.pendingPaymentInvoices = [];
      this.expandedPendingInvoices = {};
      return;
    }

    const bookingNumber =
      this.orderDetail?.invoiceNo ||
      this.apiData?.booking_number ||
      this.apiData?.invoice_number ||
      '';
    const orderNumber =
      this.orderDetail?.orderNo || this.apiData?.order_number || '';

    if (!bookingNumber && !orderNumber) {
      this.pendingPaymentInvoices = [];
      this.expandedPendingInvoices = {};
      return;
    }

    const payload = bookingNumber
      ? { booking_number: bookingNumber }
      : { order_number: orderNumber };

    this.sub.add(
      this.apiService.getPendingBookingAmount(payload).subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.pendingPaymentInvoices = [];
            this.expandedPendingInvoices = {};
            return;
          }

          const invoices = Array.isArray(res?.data) ? res.data : [];
          this.pendingPaymentInvoices = invoices;
          this.expandedPendingInvoices = invoices.reduce(
            (acc: { [key: string]: boolean }, invoice: PendingPaymentInvoice) => {
              acc[this.getPendingPaymentCardKey(invoice)] = false;
              return acc;
            },
            {},
          );
          this.cdr.detectChanges();
        },
        error: () => {
          this.pendingPaymentInvoices = [];
          this.expandedPendingInvoices = {};
        },
      }),
    );
  }

  private mapApiResponseToOrderDetail(apiData: ApiOrderResponse): OrderDetail {
    const userName =
      `${this.userData?.first_name || ''} ${this.userData?.last_name || ''}`.trim();
    const invoiceItems = Array.isArray(apiData?.invoice_items)
      ? apiData.invoice_items
      : [];

    // Map invoice items
    const items: OrderItem[] = invoiceItems.map((item) => ({
      id: item?.item_id || '',
      name: item.part_desc,
      price: this.extractPriceFromString(item.item_amount),
      image: item.image_url,
      engine: '',
      seller: apiData?.seller_detail,
      grade: item?.quality || '',
      brand: item?.make || '',
      model: item?.model || '',
      year: item?.year_of_manufacturing || '',
      invoiceNumber: apiData?.invoice_number || '',
      quantity: item?.total_quantity || '',
      total_quantity: item?.total_quantity || '',
      part_no: item?.part_no || '',
      available_quantity: item?.available_quantity,
      integration_id: item?.integration_id || '',
      item_id: item?.item_id || '',
      booking_amount: item?.booking_amount || '',
    }));

    // Map addresses
    const addresses = apiData?.order_address || [];
    const byType = (t: string) =>
      addresses.find((addr) => addr?.order_address_type === t);
    const billingAddress = byType('billing_address');
    const shippingAddress = byType('shipping_address');
    const storeAddress = byType('store_address');

    const isStorePickup = this.resolveIsStorePickup(apiData, items);
    const resolvedSellerAddress: Address = this.extractSellerAddress(apiData, items);

    const seller = apiData?.seller_detail;
    const sellerAddress: Address | null = seller
      ? {
        name: seller.seller_name,
        pincode: seller.pincode,
        address: [
          seller.address_1,
          seller.address_2,
          seller.address_3,
          seller.city,
          seller.state,
        ]
          .filter(Boolean)
          .join(', '),
        type: 'Other',
      }
      : null;
    const userBilling = this.getUserBillingAddress();
    const userShipping = this.getUserShippingAddress();

    const mappedBillingAddress: Address = (billingAddress?.address_1 || billingAddress?.company_name || billingAddress?.receiver_name)
      ? {
        name:
          billingAddress?.company_name ||
          billingAddress?.receiver_name ||
          userName,
        pincode: billingAddress?.pincode || '',
        address:
          `${billingAddress?.address_1 || ''} ${billingAddress?.city || ''} ${billingAddress?.state || ''}`.trim(),
        gst: billingAddress?.gstin_number || undefined,
        type: (billingAddress?.address_type as 'Home' | 'Work' | 'Other') || ('Work' as const),
      }
      : (userBilling.address || userBilling.name)
        ? userBilling
        : { name: userName, pincode: '', address: '' };

    const mappedShippingAddress: Address = isStorePickup
      ? (storeAddress
        ? {
          name:
            storeAddress?.receiver_name ||
            seller?.seller_name ||
            userName,
          pincode: storeAddress?.pincode || '',
          address: [
            storeAddress?.address_1,
            storeAddress?.address_2,
            storeAddress?.address_3,
            storeAddress?.city,
            storeAddress?.state,
            storeAddress?.pincode,
          ]
            .filter(Boolean)
            .join(', ') || storeAddress?.address_1 || '',
          type: 'Other',
        }
        : sellerAddress || { name: seller?.seller_name || '', pincode: seller?.pincode || '', address: '' })
      : ((shippingAddress?.address_1 || shippingAddress?.receiver_name || shippingAddress?.company_name)
        ? {
          name: shippingAddress?.receiver_name || shippingAddress?.company_name || userName,
          pincode: shippingAddress?.pincode || '',
          address:
            `${shippingAddress?.address_1 || ''} ${shippingAddress?.city || ''} ${shippingAddress?.state || ''}`.trim(),
          gst: shippingAddress?.gstin_number || undefined,
          type: (shippingAddress?.address_type as 'Home' | 'Work' | 'Other') || ('Home' as const),
        }
        : (userShipping.address || userShipping.name)
          ? userShipping
          : { name: userName, pincode: '', address: '' });

    // Price: use price_breakup if present, else fall back to invoice-level amounts
    const pb = apiData?.price_breakup;
    const total = pb
      ? this.extractPriceFromString(pb.total_amount)
      : this.extractPriceFromString(
        apiData?.invoice_amount ?? apiData?.overall_invoice_amount,
      );
    const subtotal = pb ? this.extractPriceFromString(pb.sub_total) : total;
    const discount = this.extractPriceFromString(pb?.discount_on_base_price);
    const platform = this.extractPriceFromString(pb?.platform_charges);
    const delivery =
      pb?.delivery_charges === 'FREE'
        ? 0
        : this.extractPriceFromString(
          pb?.delivery_charges ?? apiData?.delivery_charges,
        );
    const gst = this.extractPriceFromString(pb?.gst);
    const scrap = this.extractPriceFromString(pb?.scrap_booking_amount);

    const priceBreakdown: PriceBreakdown = {
      subtotal,
      discount,
      platformCharges: platform,
      deliveryCharges: delivery,
      gst,
      total,
      scrap_booking_amount: scrap,
      base_price: subtotal,
      gst_amount: gst,
      platform_charges: platform,
      delivery_charges: delivery,
      order_total: total,
    };

    const isDone = ['Delivered', 'Picked Up'].includes(apiData?.invoice_status);

    const status: OrderStatus[] = [];

    return {
      id: apiData?.order_number,
      invoiceNo: apiData?.invoice_number,
      orderNo: apiData?.order_number,
      estimatedDelivery:
        (isDone && apiData?.delivery_date) ||
        apiData?.tracking_dt ||
        apiData?.delivery_date ||
        (isDone ? '' : this.getFutureTwoDaysDate()) ||
        '',
      status: status,
      items: items,
      billingAddress: mappedBillingAddress,
      shippingAddress: mappedShippingAddress,
      sellerAddress: resolvedSellerAddress,
      storeAddress: resolvedSellerAddress,
      priceBreakdown: priceBreakdown,
      canCancel: apiData?.invoice_status !== 'Delivered',
      isStorePickup,
      isBooking: this.isBookingFlow || this.shouldFetchBookingDetail(),
      isEnquiry: this.isEnquiry(),
      invoicePdfPath: apiData?.invoice_pdf_path,
      invoiceRating: apiData?.invoice_rating,
      invoice_status: apiData?.invoice_status,
      generated_pin: apiData?.generated_pin ?? (apiData as any)?.pin ?? (apiData as any)?.pickup_pin ?? this.apiData?.generated_pin ?? '',
    };
  }

  private extractPriceFromString(priceString: any): number {
    if (priceString === null || priceString === undefined) {
      return 0;
    }

    if (typeof priceString === 'number') {
      return priceString;
    }

    const cleanPrice = String(priceString).replace(/[₹,\s]/g, '');
    return parseFloat(cleanPrice) || 0;
  }

  getBookingPercentage(): string {
    if (this.orderDetail?.booking_percent_rule) {
      return this.orderDetail.booking_percent_rule.toString();
    }
    if (this.orderDetail?.priceBreakdown?.booking_percent_rule) {
      return this.orderDetail.priceBreakdown.booking_percent_rule.toString();
    }
    const booking = Number(this.orderDetail?.priceBreakdown?.scrap_booking_amount || 0);
    const base = Number(
      (this.orderDetail?.priceBreakdown?.base_price && this.orderDetail.priceBreakdown.base_price > 0)
        ? this.orderDetail.priceBreakdown.base_price
        : (this.orderDetail?.priceBreakdown?.order_total || 0),
    );
    if (booking > 0 && base > 0) {
      const pct = Math.round((booking / base) * 100);
      if (pct > 0 && pct <= 100) return pct.toString();
    }
    return '';
  }

  formatPrice(price: number | string | undefined | null): string {
    const n = this.extractPriceFromString(price);
    return `₹${n.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  }

  getStatusIconForTimeline(
    iconType: string,
    isCompleted: boolean = false,
  ): SafeHtml {
    const strokeColor = isCompleted ? '#307FE1' : '#C0C5C7';
    const icons: { [key: string]: string } = {
      'order-placed': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.2795 9.02519V14.5714C15.2795 15.8338 14.2116 16.8571 12.8944 16.8571H11.7019M15.2795 9.02519C15.5832 8.87531 15.8869 8.64862 16.1463 8.42569C16.6156 8.02234 16.6875 7.36547 16.4056 6.82513L15.1437 4.40633C14.7396 3.63197 13.9138 3.14282 13.0104 3.14282H6.41819C5.51479 3.14282 4.68893 3.63197 4.28491 4.40633L3.02294 6.82513C2.74103 7.36547 2.81296 8.02234 3.28226 8.42569C3.54166 8.64862 3.84538 8.87531 4.14911 9.02519M15.2795 9.02519C15.0186 9.15392 14.7475 9.23806 14.4844 9.23806C12.8944 9.23806 12.0994 7.71425 12.0994 7.71425C12.0994 7.71425 11.3043 9.23806 9.71429 9.23806C8.12423 9.23806 7.32921 7.71425 7.32921 7.71425C7.32921 7.71425 6.53418 9.23806 4.94413 9.23806C4.68109 9.23806 4.40998 9.15392 4.14911 9.02519M4.14911 9.02519V14.5714C4.14911 15.8338 5.21694 16.8571 6.53418 16.8571H7.72672M7.72672 16.8571V13.0476C7.72672 12.6268 8.08267 12.2857 8.52175 12.2857H10.9068C11.3459 12.2857 11.7019 12.6268 11.7019 13.0476V16.8571M7.72672 16.8571H11.7019" stroke="${strokeColor}" stroke-width="1.14286" stroke-linejoin="round"/></svg>`,
      shipped: `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.2795 9.02519V14.5714C15.2795 15.8338 14.2116 16.8571 12.8944 16.8571H11.7019M15.2795 9.02519C15.5832 8.87531 15.8869 8.64862 16.1463 8.42569C16.6156 8.02234 16.6875 7.36547 16.4056 6.82513L15.1437 4.40633C14.7396 3.63197 13.9138 3.14282 13.0104 3.14282H6.41819C5.51479 3.14282 4.68893 3.63197 4.28491 4.40633L3.02294 6.82513C2.74103 7.36547 2.81296 8.02234 3.28226 8.42569C3.54166 8.64862 3.84538 8.87531 4.14911 9.02519M15.2795 9.02519C15.0186 9.15392 14.7475 9.23806 14.4844 9.23806C12.8944 9.23806 12.0994 7.71425 12.0994 7.71425C12.0994 7.71425 11.3043 9.23806 9.71429 9.23806C8.12423 9.23806 7.32921 7.71425 7.32921 7.71425C7.32921 7.71425 6.53418 9.23806 4.94413 9.23806C4.68109 9.23806 4.40998 9.15392 4.14911 9.02519M4.14911 9.02519V14.5714C4.14911 15.8338 5.21694 16.8571 6.53418 16.8571H7.72672M7.72672 16.8571V13.0476C7.72672 12.6268 8.08267 12.2857 8.52175 12.2857H10.9068C11.3459 12.2857 11.7019 12.6268 11.7019 13.0476V16.8571M7.72672 16.8571H11.7019" stroke="${strokeColor}" stroke-width="1.14286" stroke-linejoin="round"/></svg>`,
      'in-transit': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_5668_53583)"><path d="M2.85714 6.19044L9.71429 3.14282L13.1429 4.66663M2.85714 6.19044V13.8095L9.71429 16.8571M2.85714 6.19044L6.28571 7.71425M16.5714 6.19044L9.71429 9.23806M16.5714 6.19044L13.1429 4.66663M16.5714 6.19044V10.3809M9.71429 9.23806V16.8571M9.71429 9.23806L6.28571 7.71425M9.71429 16.8571L11 16.2857M6.28571 7.71425L13.1429 4.66663" stroke="${strokeColor}" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 14.1905H18.0952M18.0952 14.1905L16.1905 12.2858M18.0952 14.1905L16.1905 16.0953" stroke="${strokeColor}" stroke-width="0.857143" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_5668_53583"><rect width="18.2857" height="18.2857" fill="white" transform="translate(0.571426 0.857178)"/></clipPath></defs></svg>`,
      'out-for-delivery': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.2381 14.5714V8.47621M11.2381 14.5714H12.7619M11.2381 14.5714H8.57143M4.38095 14.5714H5.52381M11.2381 8.47621V6.19049C11.2381 5.76971 10.897 5.42859 10.4762 5.42859H4.38095C3.96016 5.42859 3.61905 5.76971 3.61905 6.19049V7.7143M11.2381 8.47621H14.4164C14.8206 8.47621 15.2082 8.63675 15.4939 8.92252L16.887 10.3156C17.1728 10.6014 17.3333 10.989 17.3333 11.3931V13.8095C17.3333 14.2303 16.9922 14.5714 16.5714 14.5714H15.8095M8.57143 14.5714C8.57143 15.413 7.8892 16.0953 7.04762 16.0953C6.20604 16.0953 5.52381 15.413 5.52381 14.5714M8.57143 14.5714C8.57143 13.7299 7.8892 13.0476 7.04762 13.0476C6.20604 13.0476 5.52381 13.7299 5.52381 14.5714M15.8095 14.5714C15.8095 15.413 15.1273 16.0953 14.2857 16.0953C13.4441 13.0476 12.7619 15.413 12.7619 14.5714M15.8095 14.5714C15.8095 13.7299 15.1273 13.0476 14.2857 13.0476C13.4441 13.0476 12.7619 13.7299 12.7619 14.5714M3.61905 7.7143H6.66667M3.61905 7.7143H2.09524M8.19048 10H3.61905M4 12.2857H2.85714" stroke="${strokeColor}" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      delivered: `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.98873 13.4031H7.91479M8.98873 13.4031H10.193C10.7972 13.4031 11.3766 13.1656 11.8039 12.7428L14.6269 9.94917C15.0717 9.50896 15.793 9.50896 16.2378 9.94917C16.6826 10.3894 16.6826 11.1031 16.2378 11.5433L13.0948 14.6536C11.6683 16.0653 9.74242 16.8571 7.73553 16.8571H2.85714M8.98873 13.4031C9.58185 13.4031 10.0627 12.9273 10.0627 12.3404C10.0627 11.7534 9.58185 11.2776 8.98873 11.2776H6.71057C6.10636 11.2776 5.5269 11.5151 5.09966 11.9379L4.73236 12.3014C4.20412 12.8242 3.56223 13.1769 2.85714 13.3247M6.66667 3.14282H4.38095C3.96016 3.14282 3.61905 3.48394 3.61905 3.90473V7.71425C3.61905 8.13504 3.96016 8.47616 4.38095 8.47616H12C12.4208 8.47616 12.7619 8.13504 12.7619 7.71425V3.90473C12.7619 3.48394 12.4208 3.14282 12 3.14282H9.71429M6.66667 3.14282V6.19044L8.19048 5.80949L9.71429 6.19044V3.14282M6.66667 3.14282H9.71429" stroke="${strokeColor}" stroke-width="1.14286" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      'order-cancelled':
        '<svg width="14" height="15" class="md:w-[14px] md:h-[15px]" viewBox="5 5 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2577_5265)"><path d="M4.21484 8.28115L14.5006 3.70972L19.6434 5.99543M4.21484 8.28115V19.7097L14.5006 24.2811M4.21484 8.28115L9.3577 10.5669M24.7863 8.28115L14.5006 12.8526M24.7863 8.28115L19.6434 5.99543M24.7863 8.28115V14.5669M14.5006 12.8526V24.2811M14.5006 12.8526L9.3577 10.5669M14.5006 24.2811L17.072 23.1383M9.3577 10.5669L19.6434 5.99543M21.3577 21.9954L23.072 20.2811M24.7863 18.5669L23.072 20.2811M23.072 20.2811L21.3577 18.5669M23.072 20.2811L24.7863 21.9954" stroke="#E30613" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2577_5265"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.786133 0.28125)"/></clipPath></defs></svg>',
      'return-requested': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.75 4.75L7 3L10.25 4.75M3.75 4.75V9.25L7 11M3.75 4.75L7 6.5M10.25 4.75V9.25L7 11M10.25 4.75L7 6.5M7 6.5V11" stroke="${strokeColor}" stroke-width="0.85" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.75 10C9.75 10 10.25 9.5 11 9.75C11.75 10 12.25 10.5 12.25 10.75" stroke="${strokeColor}" stroke-width="0.85" stroke-linecap="round" stroke-linejoin="round" fill="none"/><path d="M11.75 10.25L12.25 10.75L11.75 11.5" stroke="${strokeColor}" stroke-width="0.85" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      'order-ready': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578L19.2142 6.00007M24.3571 8.28578V14.5715M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M14.0714 24.2858L15.9999 23.4286M8.9285 10.5715L19.2142 6.00007M18.6428 20.8572L20.3571 22.5715L24.3571 18.5715" stroke="${strokeColor}" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      pickup: `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2577_5537)"><path d="M17.4196 8.5379V16.8572C17.4196 18.7508 15.8179 20.2858 13.842 20.2858H12.0532M17.4196 8.5379C17.8752 8.3131 18.3308 7.9731 18.7199 7.6387C19.4238 7.0336 19.5317 6.0483 19.1089 5.2378L17.2159 1.6096C16.6099 0.4481 15.3711 -0.2856 14.016 -0.2856H4.1277C2.7726 -0.2856 1.5338 0.4481 0.9278 1.6096L-0.9652 5.2378C-1.388 6.0483 -1.2801 7.0336 -0.5762 7.6387C-0.1871 7.9731 0.2685 8.3131 0.7241 8.5379M17.4196 8.5379C17.0283 8.731 16.6216 8.8572 16.2271 8.8572C13.842 8.8572 12.6495 6.5715 12.6495 6.5715C12.6495 6.5715 11.4569 8.8572 9.0718 8.8572C6.6868 8.8572 5.4942 6.5715 5.4942 6.5715C5.4942 6.5715 4.3017 8.8572 1.9166 8.8572C1.5221 8.8572 1.1154 8.731 0.7241 8.5379M0.7241 8.5379V16.8572C0.7241 18.7508 2.3258 20.2858 4.3017 20.2858H6.0905M6.0905 20.2858V14.5715C6.0905 13.9403 6.6244 13.4286 7.283 13.4286H10.8607C11.5193 13.4286 12.0532 13.9403 12.0532 14.5715V20.2858M6.0905 20.2858H12.0532" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2577_5537"><rect width="17.4286" height="17.4286" fill="white" transform="translate(0.357422 0.285645)"/></clipPath></defs></svg>`,
      'refund-credited': `<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_7870_9754)"><path d="M9.0718 18.0001V15.164M9.0718 15.164C9.0718 14.3456 8.7467 13.5607 8.1681 12.982L5.9861 10.8001C5.4181 10.232 4.49705 10.232 3.92899 10.8001C3.36093 10.3682 3.36093 11.2892 3.92899 11.8572M9.0718 15.164C9.0718 14.3456 9.3969 13.5607 9.9756 12.982L12.1576 10.8001C12.7256 10.232 13.6466 10.232 14.2147 10.8001C14.7828 10.3682 14.7828 11.2892 14.2147 11.8572M3.92899 11.8572L4.95756 12.8858M3.92899 11.8572L2.77563 10.7039C2.19695 10.1252 1.87185 9.3403 1.87185 8.522V3.11439C1.87185 2.26229 1.18109 1.57153 0.32899 1.57153C-0.52311 1.57153 -1.21387 2.26229 -1.21387 3.11439V12.122C-1.21387 12.9403 -0.88877 13.7252 -0.31008 14.3039L2.38613 17.0001M14.2147 11.8572L13.1861 12.8858M14.2147 11.8572L15.3681 10.7039C15.9467 10.1252 16.2718 9.3403 16.2718 8.522V3.11439C16.2718 2.26229 16.9626 1.57153 17.8147 1.57153C18.6668 1.57153 19.3576 2.26229 19.3576 3.11439V12.122C19.3576 12.9403 19.0325 13.7252 18.4538 14.3039L15.7576 17.0001" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.6714 1.54272H6.5H8.1831C8.7783 1.54272 9.3491 1.77916 9.77 2.20002C10.1908 2.62088 10.4273 3.19169 10.4273 3.78688C10.4273 4.38207 10.1908 4.95288 9.77 5.37374C9.3491 5.7946 8.7783 6.031 8.1831 6.031H6.5L9.8662 9.3973M6.5 3.78688H12.6714" stroke="${strokeColor}" stroke-width="1.37143" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_7870_9754"><rect width="17.4286" height="17.4286" fill="white" transform="translate(0.357422 0.285645)"/></clipPath></defs></svg>`,
      'payment-pending':
        '<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7.5" r="6.5" fill="url(#pendingGradient)"/><rect x="6.5" y="4" width="1" height="5" fill="white" rx="0.5"/><circle cx="7" cy="10.5" r="0.75" fill="white"/><defs><linearGradient id="pendingGradient" x1="7" x2="7" y1="0.5" y2="14.5" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#FFD234"/><stop offset="1" stop-color="#FFA500"/></linearGradient></defs></svg>',
      'payment-successful':
        '<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7.5" r="6.5" fill="#36B548"/><path d="M4 7.5L6 9.5L10 5.5" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'payment-failed':
        '<svg width="14" height="15" class="md:w-[19px] md:h-[20px]" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7.5" r="6.5" fill="url(#errorGradient)"/><path fill="white" d="M9.207,7.5l1.644-1.644c0.195-0.195,0.195-0.512,0-0.707c-0.195-0.195-0.512-0.195-0.707,0L7.5,6.793L6.856,6.149c-0.195-0.195-0.512-0.195-0.707,0c-0.195,0.195-0.195,0.512,0,0.707L6.793,7.5l-1.644,1.644c-0.195,0.195-0.195,0.512,0,0.707c0.195,0.195,0.512,0.195,0.707,0L7.5,8.207l0.644,0.644c0.195,0.195,0.512,0.195,0.707,0c0.195-0.195,0.195-0.512,0-0.707L9.207,7.5z"/><defs><linearGradient id="errorGradient" x1="7" x2="7" y1="0.5" y2="14.5" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#ff624a"/><stop offset=".25" stop-color="#ff5940"/><stop offset=".67" stop-color="#fd4224"/><stop offset="1" stop-color="#fc2c0a"/></linearGradient></defs></svg>',
    };
    const iconHtml = icons[iconType] || icons['order-placed'];
    return this.sanitizer.bypassSecurityTrustHtml(iconHtml);
  }

  getStatusIcon(stage: string, isCompleted: boolean = false): SafeHtml {
    const strokeColor = isCompleted ? '#307FE1' : '#C0C5C7';
    const icons: { [key: string]: string } = {
      'Order Placed': `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2741_39650)"><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578V19.7144L14.0714 24.2858M24.3571 8.28578L19.2142 6.00007M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M8.9285 10.5715L19.2142 6.00007" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578V19.7144L14.0714 24.2858M24.3571 8.28578L19.2142 6.00007M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M8.9285 10.5715L19.2142 6.00007" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2741_39650"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.356934 0.285645)"/></clipPath></defs></svg>`,
      'Order Ready': `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578L19.2142 6.00007M24.3571 8.28578V14.5715M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M14.0714 24.2858L15.9999 23.4286M8.9285 10.5715L19.2142 6.00007M18.6428 20.8572L20.3571 22.5715L24.3571 18.5715" stroke="${strokeColor}" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      Shipped: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M22.1691 12.5379V20.8572C22.1691 22.7508 20.5674 24.2858 18.5915 24.2858H16.8027M22.1691 12.5379C22.6247 12.3131 23.0803 11.9731 23.4694 11.6387C24.1733 11.0336 24.2812 10.0483 23.8584 9.23782L21.9654 5.60962C21.3594 4.44808 20.1206 3.71436 18.7655 3.71436H8.87722C7.52212 3.71436 6.28332 4.44808 5.6773 5.60962L3.78434 9.23782C3.36147 10.0483 3.46937 11.0336 4.17333 11.6387C4.56242 11.9731 5.018 12.3131 5.47359 12.5379M22.1691 12.5379C21.7778 12.731 21.3712 12.8572 20.9766 12.8572C18.5915 12.8572 17.399 10.5715 17.399 10.5715C17.399 10.5715 16.2064 12.8572 13.8214 12.8572C11.4363 12.8572 10.2437 10.5715 10.2437 10.5715C10.2437 10.5715 9.05121 12.8572 6.66613 12.8572C6.27157 12.8572 5.86489 12.731 5.47359 12.5379M5.47359 12.5379V20.8572C5.47359 22.7508 7.07534 24.2858 9.05121 24.2858H10.84M10.84 24.2858V18.5715C10.84 17.9403 11.3739 17.4286 12.0326 17.4286H15.6102C16.2688 17.4286 16.8027 17.9403 16.8027 18.5715V24.2858M10.84 24.2858H16.8027" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      'In-Transit': `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2741_39063)"><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578L19.2142 6.00007M24.3571 8.28578V14.5715M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M14.0714 24.2858L15.9999 23.4286M8.9285 10.5715L19.2142 6.00007" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.5 20.2856H26.6429M26.6429 20.2856L23.7857 17.4285M26.6429 20.2856L23.7857 23.1428" stroke="${strokeColor}" stroke-width="1.28571" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2741_39063"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.356934 0.285645)"/></clipPath></defs></svg>`,
      'Out for Delivery': `<svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28" fill="none"><path d="M16.6069 20.8571V11.7143M16.6069 20.8571H18.8926M16.6069 20.8571H12.6069M6.32115 20.8571H8.03544M16.6069 11.7143V8.28568C16.6069 7.6545 16.0952 7.14282 15.464 7.14282H6.32115C5.68997 7.14282 5.17829 7.6545 5.17829 8.28568V10.5714M16.6069 11.7143H21.3744C21.9806 11.7143 22.562 11.9551 22.9906 12.3837L25.0803 14.4734C25.5089 14.902 25.7497 15.4834 25.7497 16.0896V19.7143C25.7497 20.3454 25.238 20.8571 24.6069 20.8571H23.464M12.6069 20.8571C12.6069 22.1195 11.5835 23.1428 10.3211 23.1428C9.05878 23.1428 8.03544 22.1195 8.03544 20.8571M12.6069 20.8571C12.6069 19.5947 11.5835 18.5714 10.3211 18.5714C9.05878 18.5714 8.03544 19.5947 8.03544 20.8571M23.464 20.8571C23.464 22.1195 22.4407 23.1428 21.1783 23.1428C19.9159 23.1428 18.8926 22.1195 18.8926 20.8571M23.464 20.8571C23.464 19.5947 22.4407 18.5714 21.1783 18.5714C19.9159 23.1428 18.8926 22.1195 18.8926 20.8571M5.17829 10.5714H9.74972M5.17829 10.5714H2.89258M12.0354 14H5.17829M5.74972 17.4285H4.03544" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      Delivered: `<svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.483 19.1048H11.8721M13.483 19.1048H15.2894C16.1957 19.1048 17.0649 18.7485 17.7057 18.1144L21.9403 13.9239C22.6075 13.2636 23.6894 13.2636 24.3566 13.9239C25.0239 14.5842 25.0239 15.6548 24.3566 16.3151L19.6421 20.9806C17.5023 23.0981 14.6136 24.2858 11.6032 24.2858H4.28564M13.483 19.1048C14.3727 19.1048 15.0939 18.3911 15.0939 17.5107C15.0939 16.6303 14.3727 15.9166 13.483 15.9166H10.0658C9.15947 15.9166 8.29028 16.2728 7.64942 16.907L7.09848 17.4522C6.30611 18.2364 5.34328 18.7655 4.28564 18.9872M9.99993 3.71436H6.57136C5.94018 3.71436 5.4285 4.22603 5.4285 4.85721V10.5715C5.4285 11.2027 5.94018 11.7144 6.57136 11.7144H17.9999C18.6311 11.7144 19.1428 11.2027 19.1428 10.5715V4.85721C19.1428 4.22603 18.6311 3.71436 17.9999 3.71436H14.5714M9.99993 3.71436V8.28578L12.2856 7.71436L14.5714 8.28578V3.71436M9.99993 3.71436H14.5714" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      'Order Cancelled':
        '<svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2577_5265)"><path d="M4.21484 8.28115L14.5006 3.70972L19.6434 5.99543M4.21484 8.28115V19.7097L14.5006 24.2811M4.21484 8.28115L9.3577 10.5669M24.7863 8.28115L14.5006 12.8526M24.7863 8.28115L19.6434 5.99543M24.7863 8.28115V14.5669M14.5006 12.8526V24.2811M14.5006 12.8526L9.3577 10.5669M14.5006 24.2811L17.072 23.1383M9.3577 10.5669L19.6434 5.99543M21.3577 21.9954L23.072 20.2811M24.7863 18.5669L23.072 20.2811M23.072 20.2811L21.3577 18.5669M23.072 20.2811L24.7863 21.9954" stroke="#E30613" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2577_5265"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.786133 0.28125)"/></clipPath></defs></svg>',
      'Return Requested':
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 9.5L14 6L20.5 9.5M7.5 9.5V18.5L14 22M7.5 9.5L14 13M20.5 9.5V18.5L14 22M20.5 9.5L14 13M14 13V22" stroke="#307FE1" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.5 20C19.5 20 20.5 19 22 19.5C23.5 20 24.5 21 24.5 21.5" stroke="#307FE1" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" fill="none"/><path d="M23.5 20.5L24.5 21.5L23.5 23" stroke="#307FE1" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'Picked Up': `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M22.1691 12.5379V20.8572C22.1691 22.7508 20.5674 24.2858 18.5915 24.2858H16.8027M22.1691 12.5379C22.6247 12.3131 23.0803 11.9731 23.4694 11.6387C24.1733 11.0336 24.2812 10.0483 23.8584 9.23782L21.9654 5.60962C21.3594 4.44808 20.1206 3.71436 18.7655 3.71436H8.87722C7.52212 3.71436 6.28332 4.44808 5.6773 5.60962L3.78434 9.23782C3.36147 10.0483 3.46937 11.0336 4.17333 11.6387C4.56242 11.9731 5.018 12.3131 5.47359 12.5379M22.1691 12.5379C21.7778 12.731 21.3712 12.8572 20.9766 12.8572C18.5915 12.8572 17.399 10.5715 17.399 10.5715C17.399 10.5715 16.2064 12.8572 13.8214 12.8572C11.4363 12.8572 10.2437 10.5715 10.2437 10.5715C10.2437 10.5715 9.05121 12.8572 6.66613 12.8572C6.27157 12.8572 5.86489 12.731 5.47359 12.5379M5.47359 12.5379V20.8572C5.47359 22.7508 7.07534 24.2858 9.05121 24.2858H10.84M10.84 24.2858V18.5715C10.84 17.9403 11.3739 17.4286 12.0326 17.4286H15.6102C16.2688 17.4286 16.8027 17.9403 16.8027 18.5715V24.2858M10.84 24.2858H16.8027" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      'Refund Credited':
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_7870_9754)"><path d="M14.0718 22.0001V19.164M14.0718 19.164C14.0718 18.3456 13.7467 17.5607 13.1681 16.982L10.9861 14.8001C10.4181 14.232 9.49705 14.232 8.92899 14.8001C8.36093 15.3682 8.36093 16.2892 8.92899 16.8572M14.0718 19.164C14.0718 18.3456 14.3969 17.5607 14.9756 16.982L17.1576 14.8001C17.7256 14.232 18.6466 14.232 19.2147 14.8001C19.7828 15.3682 19.7828 16.2892 19.2147 16.8572M8.92899 16.8572L9.95756 17.8858M8.92899 16.8572L7.77563 15.7039C7.19695 15.1252 6.87185 14.3403 6.87185 13.522V8.11439C6.87185 7.26229 6.18109 6.57153 5.32899 6.57153C4.47689 6.57153 3.78613 7.26229 3.78613 8.11439V17.122C3.78613 17.9403 4.11123 18.7252 4.68992 19.3039L7.38613 22.0001M19.2147 16.8572L18.1861 17.8858M19.2147 16.8572L20.3681 15.7039C20.9467 15.1252 21.2718 14.3403 21.2718 13.522V8.11439C21.2718 7.26229 21.9626 6.57153 22.8147 6.57153C23.6668 6.57153 24.3576 7.26229 24.3576 8.11439V17.122C24.3576 17.9403 24.0325 18.7252 23.4538 19.3039L20.7576 22.0001" stroke="#C0C5C7" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.6714 5.54272H11.5H13.1831C13.7783 5.54272 14.3491 5.77916 14.77 6.20002C15.1908 6.62088 15.4273 7.19169 15.4273 7.78688C15.4273 8.38207 15.1908 8.95288 14.77 9.37374C14.3491 9.7946 13.7783 10.031 13.1831 10.031H11.5L14.8662 13.3973M11.5 7.78688H17.6714" stroke="#C0C5C7" stroke-width="1.37143" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_7870_9754"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.357422 0.285645)"/></clipPath></defs></svg>',
      'Payment Pending':
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="14" r="13" fill="url(#pendingGradient)"/><rect x="13" y="8" width="2" height="10" fill="white" rx="1"/><circle cx="14" cy="21" r="1.5" fill="white"/><defs><linearGradient id="pendingGradient" x1="14" x2="14" y1="1" y2="27" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#FFD234"/><stop offset="1" stop-color="#FFA500"/></linearGradient></defs></svg>',
      'Payment Successful':
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="14" r="13" fill="#36B548"/><path d="M8 14L12 18L20 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'Payment Failed':
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="14" r="13" fill="url(#errorGradient)"/><path fill="white" d="M17.414,14l3.288-3.288c0.391-0.391,0.391-1.024,0-1.414c-0.391-0.391-1.024-0.391-1.414,0L14,12.586l-3.288-3.288c-0.391-0.391-1.024-0.391-1.414,0c-0.391,0.391-0.391,1.024,0,1.414L12.586,14l-3.288,3.288c-0.391,0.391-0.391,1.024,0,1.414c0.391,0.391,1.024,0.391,1.414,0L14,15.414l3.288,3.288c0.391,0.391,1.024,0.391,1.414,0c0.391-0.391,0.391-1.024,0-1.414L17.414,14z"/><defs><linearGradient id="errorGradient" x1="14" x2="14" y1="1" y2="27" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#ff624a"/><stop offset=".25" stop-color="#ff5940"/><stop offset=".67" stop-color="#fd4224"/><stop offset="1" stop-color="#fc2c0a"/></linearGradient></defs></svg>',
    };
    const iconHtml =
      icons[stage] ||
      '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2741_39650)"><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578V19.7144L14.0714 24.2858M24.3571 8.28578L19.2142 6.00007M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M8.9285 10.5715L19.2142 6.00007" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.78564 8.28578L14.0714 3.71436L19.2142 6.00007M3.78564 8.28578V19.7144L14.0714 24.2858M3.78564 8.28578L8.9285 10.5715M24.3571 8.28578L14.0714 12.8572M24.3571 8.28578V19.7144L14.0714 24.2858M24.3571 8.28578L19.2142 6.00007M14.0714 12.8572V24.2858M14.0714 12.8572L8.9285 10.5715M8.9285 10.5715L19.2142 6.00007" stroke="${strokeColor}" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_2741_39650"><rect width="27.4286" height="27.4286" fill="white" transform="translate(0.356934 0.285645)"/></clipPath></defs></svg>';
    return this.sanitizer.bypassSecurityTrustHtml(iconHtml);
  }

  getStatusColor(stage: string, isCompleted: boolean): string {
    if (!isCompleted) return 'text-gray-400';

    const colors: { [key: string]: string } = {
      'Order Placed': 'text-blue-600',
      'Order Ready': 'text-blue-600',
      Shipped: 'text-blue-600',
      'In-Transit': 'text-blue-600',
      'Out for Delivery': 'text-blue-600',
      Delivered: 'text-blue-600',
      'Order Cancelled': 'text-red-600',
      'Return Requested': 'text-blue-600',
      'Picked Up': 'text-blue-600',
      'Refund Credited': 'text-blue-600',
      'Payment Pending': 'text-[#EB8700]',
      'Payment Successful': 'text-green-600',
      'Payment Failed': 'text-red-600',
    };
    return colors[stage] || 'text-gray-600';
  }

  getProgressWidth(): string {
    if (!this.orderDetail) return '0%';
    const totalStages = this.orderDetail.status.length;
    if (totalStages <= 1) return '0%';
    const completedStages = this.orderDetail.status.filter(
      (s: OrderStatus) => s.isCompleted,
    ).length;
    const ratio = Math.max(0, (completedStages - 1) / (totalStages - 1));
    const percent = Math.round(ratio * 100);
    return `${percent}%`;
  }

  getTimelineInset(): string {
    const n = this.orderDetail?.status?.length || 0;
    if (n <= 1) return '0%';
    return `${(100 / (2 * n)).toFixed(4)}%`;
  }

  canCancelOrder(): boolean {
    if (!this.orderDetail) return false;
    const currentStatus = this.orderDetail.status.find(
      (s: OrderStatus) => s.isCompleted === false,
    );
    if (!currentStatus) {
      return false;
    }

    const cancellableStages = ['Order Placed', 'Shipped'];
    const currentStageIndex = this.orderDetail.status.findIndex(
      (s: OrderStatus) => s.stage === currentStatus.stage,
    );

    for (let i = 0; i <= currentStageIndex; i++) {
      if (
        this.orderDetail.status[i].stage === 'In-Transit' ||
        this.orderDetail.status[i].stage === 'Out for Delivery' ||
        this.orderDetail.status[i].stage === 'Delivered'
      ) {
        return false;
      }
    }

    return true;
  }

  cancelOrder(): void {
    if (!this.canCancelOrder()) {
      return;
    }

    this.openCancelPopup();
  }

  resolveDocumentType(): DocumentPresignedType {
    const docTypeStr = String(
      this.document_type || this.apiData?.document_type || '',
    ).toUpperCase();
    if (docTypeStr === 'PROFORMA') {
      return 'proforma';
    }
    if (docTypeStr === 'ENQUIRY') {
      return 'booking';
    }
    if (docTypeStr === 'BOOKING' || this.isBookingFlow) {
      return 'booking';
    }
    return 'tax_invoice';
  }

  resolveDocumentData(type: DocumentPresignedType) {
    const invoiceNumber = (
      this.orderDetail?.invoiceNo ||
      this.apiData?.invoice_number ||
      this.chekcInvoice_umnber ||
      ''
    ).trim();

    const bookingNumber = (
      this.apiData?.booking_number ||
      this.apiData?.booking_no ||
      this.orderDetail?.orderNo ||
      this.apiData?.order_number ||
      ''
    ).trim();

    const creditNoteNumber = (
      this.apiData?.credit_note_number ||
      (this.apiData as any)?.credit_note_no ||
      (this.orderDetail as any)?.credit_note_number ||
      ''
    ).trim();

    switch (type) {
      case 'tax_invoice':
        return { invoice_number: invoiceNumber };
      case 'booking':
        return { booking_number: bookingNumber };
      case 'proforma':
        return { booking_number: bookingNumber };
      case 'credit_note':
        return {
          invoice_number: invoiceNumber,
          credit_note_number: creditNoteNumber,
        };
    }
  }

  canDownloadDocument(): boolean {
    return !!(
      this.orderDetail?.invoicePdfPath ||
      this.orderDetail?.invoiceNo ||
      this.apiData?.invoice_number ||
      this.apiData?.booking_number ||
      this.apiData?.booking_no ||
      this.orderDetail?.orderNo
    );
  }

  canDownloadCreditNote(): boolean {
    return !!(
      this.apiData?.credit_note_number ||
      (this.apiData as any)?.credit_note_no ||
      (this.orderDetail as any)?.credit_note_number
    );
  }

  getDocumentButtonLabel(): string {
    const docTypeStr = String(
      this.document_type || this.apiData?.document_type || '',
    ).toUpperCase();
    if (docTypeStr === 'ENQUIRY') return 'Enquiry';
    const type = this.resolveDocumentType();
    if (type === 'proforma') return 'Proforma';
    if (type === 'booking') return 'Booking';
    if (type === 'credit_note') return 'Credit Note';
    return 'Invoice';
  }

  downloadInvoice(): void {
    if (this.isDocumentLoading) return;

    const docType = this.resolveDocumentType();
    const docData = this.resolveDocumentData(docType);

    this.isDocumentLoading = true;
    this.sub.add(
      this.documentService
        .openDocument(docType, docData)
        .pipe(
          finalize(() => {
            this.isDocumentLoading = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe({
          next: () => { },
          error: () => { },
        }),
    );
  }

  downloadCreditNote(): void {
    if (this.isDocumentLoading) return;

    const docData = this.resolveDocumentData('credit_note');
    this.isDocumentLoading = true;
    this.sub.add(
      this.documentService
        .openDocument('credit_note', docData)
        .pipe(
          finalize(() => {
            this.isDocumentLoading = false;
            this.cdr.detectChanges();
          }),
        )
        .subscribe({
          next: () => { },
          error: () => { },
        }),
    );
  }

  contactSupport(): void {
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'customer-support' },
    });
  }

  getCurrentOrderStatus(): string {
    if (!this.orderDetail) return '';
    const currentStatus = this.orderDetail.status.find(
      (s: OrderStatus) => s.isCompleted === false,
    );
    return currentStatus ? currentStatus.stage : 'Delivered';
  }

  updateToOutForDelivery(): void {
    if (!this.orderDetail) return;
    this.orderDetail.status = [
      { stage: 'Order Placed', isCompleted: true, date: '20 May 2025' },
      { stage: 'Shipped', isCompleted: true, date: '25 May 2025' },
      { stage: 'In-Transit', isCompleted: true, date: '27 May 2025' },
      { stage: 'Out for Delivery', isCompleted: false, date: '29 May 2025' },
      { stage: 'Delivered', isCompleted: false, date: '' },
    ];
  }

  resetToOriginalState(): void {
    if (!this.orderDetail) return;
    this.orderDetail.status = [
      { stage: 'Order Placed', isCompleted: true, date: '20 May 2025' },
      { stage: 'Shipped', isCompleted: false, date: '' },
      { stage: 'In-Transit', isCompleted: false, date: '' },
      { stage: 'Out for Delivery', isCompleted: false, date: '' },
      { stage: 'Delivered', isCompleted: false, date: '' },
    ];
  }

  isOrderDelivered(): boolean {
    if (!this.orderDetail) return false;
    if (['Delivered', 'Picked Up'].includes(this.orderDetail.invoice_status)) {
      return true;
    }
    const deliveredStatus = this.orderDetail.status.find(
      (s: OrderStatus) => s.stage === 'Delivered',
    );
    return deliveredStatus ? deliveredStatus.isCompleted : false;
  }

  private _cachedReturnPolicyKey = '';
  private _cachedReturnPolicyDate = '';

  getReturnPolicyValidDate(): string {
    const cacheKey = `${this.orderDetail?.invoiceNo || this.orderDetail?.id || ''}_${this.orderDetail?.estimatedDelivery || ''}_${this.orderDetail?.invoice_status || ''}`;
    if (this._cachedReturnPolicyDate && this._cachedReturnPolicyKey === cacheKey) {
      return this._cachedReturnPolicyDate;
    }

    const apiReturnDate =
      this.orderDetail?.return_valid_till ||
      this.apiData?.return_valid_till ||
      this.apiData?.return_date;
    if (apiReturnDate) {
      const parsed = new Date(apiReturnDate);
      if (!isNaN(parsed.getTime())) {
        const formatted = this.formatReturnPolicyDate(parsed);
        this._cachedReturnPolicyKey = cacheKey;
        this._cachedReturnPolicyDate = formatted;
        return formatted;
      }
    }

    let deliveryDate: Date | null = null;

    if (this.orderDetail?.status && Array.isArray(this.orderDetail.status)) {
      const deliveredStatus = this.orderDetail.status.find(
        (s: OrderStatus) =>
          (s.stage === 'Delivered' || s.stage === 'Picked Up') && s.isCompleted,
      );
      if (deliveredStatus?.timestamp) {
        const d = new Date(deliveredStatus.timestamp);
        if (!isNaN(d.getTime())) deliveryDate = d;
      }
      if (!deliveryDate && deliveredStatus?.date) {
        const d = new Date(deliveredStatus.date);
        if (!isNaN(d.getTime())) deliveryDate = d;
      }
    }

    if (!deliveryDate && this.orderDetail?.estimatedDelivery) {
      const d = new Date(this.orderDetail.estimatedDelivery);
      if (!isNaN(d.getTime())) deliveryDate = d;
    }

    if (!deliveryDate && this.apiData?.delivery_date) {
      const d = new Date(this.apiData.delivery_date);
      if (!isNaN(d.getTime())) deliveryDate = d;
    }

    if (deliveryDate) {
      const returnDate = new Date(deliveryDate);
      returnDate.setDate(returnDate.getDate() + 7);

      if (returnDate.getTime() < Date.now()) {
        const todayPlus7 = new Date();
        todayPlus7.setDate(todayPlus7.getDate() + 7);
        const formatted = this.formatReturnPolicyDate(todayPlus7);
        this._cachedReturnPolicyKey = cacheKey;
        this._cachedReturnPolicyDate = formatted;
        return formatted;
      }

      const formatted = this.formatReturnPolicyDate(returnDate);
      this._cachedReturnPolicyKey = cacheKey;
      this._cachedReturnPolicyDate = formatted;
      return formatted;
    }

    const fallbackDate = new Date();
    fallbackDate.setDate(fallbackDate.getDate() + 7);
    const formatted = this.formatReturnPolicyDate(fallbackDate);
    this._cachedReturnPolicyKey = cacheKey;
    this._cachedReturnPolicyDate = formatted;
    return formatted;
  }

  private formatReturnPolicyDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  rateOrder(): void {
    this.showRatingPopup = true;
  }

  setToDelivered(): void {
    if (!this.orderDetail) return;
    this.orderDetail.status = [
      { stage: 'Order Placed', isCompleted: true, date: '20 May 2025' },
      { stage: 'Shipped', isCompleted: true, date: '25 May 2025' },
      { stage: 'In-Transit', isCompleted: true, date: '27 May 2025' },
      { stage: 'Out for Delivery', isCompleted: true, date: '29 May 2025' },
      { stage: 'Delivered', isCompleted: true, date: '30 May 2025' },
    ];
  }

  resetRating(): void {
    this.ratingSubmitted = false;
  }

  returnRefund(): void {
    this.openReturnPopup();
  }

  openReturnPopup(): void {
    this.showReturnPopup = true;
    this.returnStep = 'select';
    this.resetReturnData();
  }

  closeReturnPopup(): void {
    this.showReturnPopup = false;
    this.resetReturnData();
  }

  resetReturnData(): void {
    this.returnStep = 'select';
    this.canProceedReturn = false;
    this.currentReturnProductIndex = 0;
    this.selectedReturnProducts = [];
    this.currentReturnProduct = null;
    this.isLastReturnProduct = false;

    if (this.orderDetail) {
      this.orderDetail.items.forEach((item: OrderItem) => {
        item.selectedForReturn = false;
        item.returnReason = undefined;
        item.returnComment = '';
        item.returnRefundAmount = 0;
        item.uploadedFiles = [];
        item.uploadedFileUrls = [];
      });
    }
  }

  updateProceedReturnButton(): void {
    if (!this.orderDetail) return;
    this.canProceedReturn = this.orderDetail.items.some(
      (item: OrderItem) => item.selectedForReturn,
    );
  }

  proceedToReturnDetails(): void {
    if (!this.canProceedReturn || !this.orderDetail) return;

    this.selectedReturnProducts = this.orderDetail.items.filter(
      (item: OrderItem) => item.selectedForReturn,
    );
    this.currentReturnProductIndex = 0;
    this.currentReturnProduct = this.selectedReturnProducts[0];
    if (this.currentReturnProduct) {
      if (!this.currentReturnProduct.returnComment) {
        this.currentReturnProduct.returnComment = '';
      }
      if (!this.currentReturnProduct.uploadedFileUrls) {
        this.currentReturnProduct.uploadedFileUrls = [];
      }
    }
    this.isLastReturnProduct = this.selectedReturnProducts.length === 1;
    this.returnStep = 'details';
  }

  backToReturnSelection(): void {
    this.returnStep = 'select';
  }

  selectReturnReason(reason: string | undefined): void {
    if (this.currentReturnProduct && reason) {
      this.currentReturnProduct.returnReason = reason;
      this.updateReturnRefundAmount();
    }
  }

  updateReturnRefundAmount(): void {
    if (this.currentReturnProduct && this.currentReturnProduct.returnReason) {
      const selectedReason = this.returnReasons.find(
        (r) => r.value === this.currentReturnProduct?.returnReason,
      );
      if (selectedReason) {
        const refundAmount = this.currentReturnProduct.booking_amount;

        this.currentReturnProduct.returnRefundAmount = Math.max(
          0,
          refundAmount,
        );
      }
    } else if (this.currentReturnProduct) {
      this.currentReturnProduct.returnRefundAmount = 0;
    }
  }

  onFileSelected(event: any, product: OrderItem): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = [
        'video/mp4',
        'video/avi',
        'video/mov',
        'video/mkv',
        'video/webm',
      ];
      const isValidFile =
        validImageTypes.includes(file.type) ||
        validVideoTypes.includes(file.type);

      if (!isValidFile) {
        this.notificationService.error(
          'Please select only JPG/PNG/MP4/AVI/MOV/MKV/WEBM files',
        );
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        this.notificationService.error('File size should not exceed 50MB');
        return;
      }

      if (!product.uploadedFiles) product.uploadedFiles = [];
      if (!product.uploadedFileUrls) product.uploadedFileUrls = [];

      product.uploadedFiles.push(file);

      const reader = new FileReader();
      reader.onload = (e: any) => {
        product.uploadedFileUrls!.push(e.target.result);
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);

      event.target.value = '';
    }
  }

  removeFile(product: OrderItem, index: number): void {
    if (product.uploadedFiles && product.uploadedFileUrls) {
      product.uploadedFiles.splice(index, 1);
      product.uploadedFileUrls.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  private async convertFilesToBase64(files: File[]): Promise<string[]> {
    const base64Promises = files.map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    return Promise.all(base64Promises);
  }

  getSelectedReturnReasonMessage(): string {
    if (this.currentReturnProduct?.returnReason) {
      const reason = this.returnReasons.find(
        (r) => r.value === this.currentReturnProduct?.returnReason,
      );
      return reason?.message || '';
    }
    return '';
  }

  getTotalSelectedProductsRefund(): number {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return 0;
    }

    return this.selectedProducts.reduce((total, product) => {
      return total + (product.refundAmount || 0);
    }, 0);
  }

  getSelectedProductsWithRefund(): OrderItem[] {
    if (!this.selectedProducts) {
      return [];
    }

    return this.selectedProducts.filter(
      (product) => product.refundAmount && product.refundAmount > 0,
    );
  }

  canProceedToNextReturn(): boolean {
    if (!this.currentReturnProduct) return false;

    if (!this.currentReturnProduct.returnReason) return false;

    if (
      !this.currentReturnProduct.uploadedFiles ||
      this.currentReturnProduct.uploadedFiles.length === 0
    )
      return false;

    return true;
  }

  proceedToNextReturnProduct(): void {
    if (!this.canProceedToNextReturn()) return;

    if (this.isLastReturnProduct) {
      this.submitReturn().catch((error) => {
        console.error('Error submitting return:', error);
        this.loaderService.hide();
      });
    } else {
      this.currentReturnProductIndex++;
      this.currentReturnProduct =
        this.selectedReturnProducts[this.currentReturnProductIndex];
      if (this.currentReturnProduct) {
        if (!this.currentReturnProduct.returnComment) {
          this.currentReturnProduct.returnComment = '';
        }
        if (!this.currentReturnProduct.uploadedFileUrls) {
          this.currentReturnProduct.uploadedFileUrls = [];
        }
      }
      this.isLastReturnProduct =
        this.currentReturnProductIndex ===
        this.selectedReturnProducts.length - 1;
    }
  }

  async submitReturn(): Promise<void> {
    const returnData = await Promise.all(
      this.selectedReturnProducts.map(async (product) => {
        return {
          invoice_line_id: product.item_id,
          quantity: product?.total_quantity || '1',
          return_reason: product.returnReason,
          comment: product.returnComment,
        };
      }),
    );

    const payload = {
      order_number: this.orderDetail?.orderNo,
      invoice_number: this.orderDetail?.invoiceNo,
      invoice_list: returnData,
    };

    this.loaderService.show();

    try {
      this.sub.add(
        this.apiService.submitReturnRequest(payload).subscribe({
          next: (res) => {
            this.uploadImages();

            this.returnResponse = res;

            this.closeReturnPopup();
            this.loaderService.hide();
          },
          error: (err) => {
            this.notificationService.error(
              err.error?.data?.msg ||
              'Failed to submit return request. Please try again later.',
            );
            console.error('Failed to submit return request', err);
            this.loaderService.hide();
          },
        }),
      );
    } catch (error: any) {
      console.error('Error in submitReturn:', error);
      this.notificationService.error(
        error.error?.data?.msg ||
        'An unexpected error occurred while submitting return. Please try again.',
      );
      this.loaderService.hide();
    }
  }

  async uploadImages(): Promise<void> {
    const formData = new FormData();
    const invoice_line_id = this.selectedReturnProducts.map(
      (item: any) => item.item_id,
    );
    console.log('invoice_line_id', invoice_line_id);

    formData.append('invoice_number', this.orderDetail?.invoiceNo || '');
    formData.append('invoice_line_id_list', JSON.stringify(invoice_line_id));
    const uniquePartNumbers = [
      ...new Set(
        this.selectedReturnProducts
          .map((product) => product.part_no)
          .filter(Boolean),
      ),
    ];

    formData.append('part_numbers', JSON.stringify(uniquePartNumbers));

    this.selectedReturnProducts.forEach((product) => {
      const partNumber = product.part_no;
      if (partNumber && product.uploadedFiles?.length) {
        product.uploadedFiles.forEach((file: File, index: number) => {
          formData.append(`${partNumber + '_' + index}`, file, file.name);
        });
      }
    });

    this.loaderService.show();

    try {
      this.sub.add(
        this.apiService.uploadReturnImage(formData).subscribe({
          next: (res) => {
            this.closeReturnPopup();
            this.loaderService.hide();
            this.router.navigate(['/order-status'], {
              queryParams: {
                order_id:
                  this.apiData?.order_number || this.orderDetail?.orderNo,
                returOrder: true,
                invoice_numbers:
                  this.returnResponse?.data?.invoice_numbers ||
                  this.orderDetail?.invoiceNo ||
                  this.apiData?.invoice_number ||
                  this.chekcInvoice_umnber,
                status:
                  this.returnResponse?.data?.status ||
                  this.orderDetail?.invoice_status ||
                  this.apiData?.invoice_status,
                invoice_line_id: invoice_line_id,
              },
            });
          },
          error: (err) => {
            console.error('Failed to submit return request', err);
            this.notificationService.error(
              err.error?.data?.msg ||
              'Failed to upload return images. Please try again.',
            );
            this.loaderService.hide();
          },
        }),
      );
    } catch (error: any) {
      console.error('Error in submitReturn:', error);
      this.notificationService.error(
        error.error?.data?.msg ||
        'An unexpected error occurred while uploading images. Please try again.',
      );
      this.loaderService.hide();
    }
  }

  openCancelPopup(): void {
    this.showCancelPopup = true;
    this.cancelStep = 'select';
    this.resetCancellationData();
  }

  closeCancelPopup(): void {
    this.showCancelPopup = false;
    this.resetCancellationData();
  }

  resetCancellationData(): void {
    this.cancelStep = 'select';
    this.canProceed = false;
    this.currentProductIndex = 0;
    this.selectedProducts = [];
    this.currentProduct = null;
    this.isLastProduct = false;

    if (this.orderDetail) {
      this.orderDetail.items.forEach((item: OrderItem) => {
        item.selectedForCancel = false;
        item.selectedReason = undefined;
        item.cancellationComment = '';
        item.refundAmount = 0;
      });
    }
  }

  updateProceedButton(): void {
    if (!this.orderDetail) return;

    this.canProceed = this.orderDetail.items.some(
      (item: OrderItem) => item.selectedForCancel,
    );
  }

  proceedToDetails(): void {
    if (!this.canProceed || !this.orderDetail) return;

    this.selectedProducts = this.orderDetail.items.filter(
      (item: OrderItem) => item.selectedForCancel,
    );
    this.currentProductIndex = 0;
    this.currentProduct = this.selectedProducts[0];
    this.isLastProduct = this.selectedProducts.length === 1;
    this.cancelStep = 'details';
    console.log('Selected Products for Cancellation:', this.selectedProducts);

    this.updateProceedButton();
  }

  backToSelection(): void {
    this.cancelStep = 'select';
  }

  selectReason(reason: string, product?: OrderItem): void {
    if (product) {
      product.selectedReason = reason;
      product.refundAmount = product.price;
    }
    else if (this.currentProduct) {
      this.currentProduct.selectedReason = reason;
      this.currentProduct.refundAmount = this.currentProduct.booking_amount;
    }

    this.updateRefundAmount();
  }

  recalculateAllRefunds(): void {
    if (this.selectedProducts && this.selectedProducts.length > 0) {
      this.selectedProducts.forEach((product) => {
        if (product.selectedReason) {
          product.refundAmount = product.price;
        } else {
          product.refundAmount = 0;
        }
      });
    }
  }

  allProductsHaveReasons(): boolean {
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      return false;
    }
    return this.selectedProducts.every((product) => product.selectedReason);
  }

  getProductsWithoutReasons(): OrderItem[] {
    if (!this.selectedProducts) return [];
    return this.selectedProducts.filter((product) => !product.selectedReason);
  }

  selectReasonForProduct(reason: string, product: OrderItem): void {
    product.selectedReason = reason;
    product.refundAmount = product.price;
    this.recalculateAllRefunds();
    this.updateProceedButton();
  }

  selectReasonForAllProducts(reason: string): void {
    if (this.selectedProducts && this.selectedProducts.length > 0) {
      this.selectedProducts.forEach((product) => {
        product.selectedReason = reason;
        product.refundAmount = product.price;
        console.log(
          `Product: ${product.name}, Reason: ${reason}, Price: ${product.price}, RefundAmount: ${product.refundAmount}`,
        );
      });
      this.recalculateAllRefunds();
      this.updateProceedButton();
      console.log(
        'Total Refund Amount:',
        this.getTotalSelectedProductsRefund(),
      );
    }
  }

  updateRefundAmount(): void {
    console.log(
      'Updating refund amount for current product:',
      this.currentProduct,
    );
    if (this.currentProduct && this.currentProduct.selectedReason) {
      this.currentProduct.refundAmount = this.currentProduct.booking_amount;
    } else if (this.currentProduct) {
      this.currentProduct.refundAmount = 0;
    }

    if (this.selectedProducts && this.selectedProducts.length > 0) {
      this.selectedProducts.forEach((product) => {
        if (
          product.selectedReason &&
          (!product.refundAmount || product.refundAmount === 0)
        ) {
          product.refundAmount = product.price;
        }
      });
    }
  }

  proceedToNextProduct(): void {
    if (!this.currentProduct?.selectedReason) return;

    if (this.isLastProduct) {
      this.submitCancellation();
    } else {
      this.currentProductIndex++;
      this.currentProduct = this.selectedProducts[this.currentProductIndex];
      this.isLastProduct =
        this.currentProductIndex === this.selectedProducts.length - 1;
    }
  }

  submitCancellation(): void {
    const items = this.selectedProducts.map((product) => ({
      invoice_line_id: product.item_id,
      reason: product.selectedReason,
      comment: product.cancellationComment,
    }));

    const payload = {
      booking_number: this.orderDetail.invoiceNo,
      order_number: this.orderDetail.orderNo,
      user_id: this.userData?.id,
      cancel_parts_detail: items,
    };
    this.loaderService.show();

    this.sub.add(
      this.apiService.cancelOrder(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.orderancel = true;
            this.closeCancelPopup();
            this.loaderService.hide();
            this.router.navigate(['/order-status'], {
              queryParams: {
                order_id:
                  this.apiData?.order_number || this.orderDetail?.orderNo,
                orderCancel: true,
                invoice_numbers:
                  res.data?.invoice_number ||
                  this.orderDetail?.invoiceNo ||
                  this.apiData?.invoice_number ||
                  this.chekcInvoice_umnber,
                status:
                  res.data?.invoice_status ||
                  this.orderDetail?.invoice_status ||
                  this.apiData?.invoice_status,
                invoice_line_id: items.map((item: any) => item.invoice_line_id),
              },
            });
          }
        },
        error: (error: any) => {
          console.error('Cancellation error:', error);
          this.closeCancelPopup();
          this.notificationService.error(
            error?.error?.data?.msg ||
            'An error occurred while cancelling the order. Please try again.',
          );
        },
      }),
    );
  }

  private normalizeInvoiceResponse(data: any): ApiOrderResponse | null {
    if (data?.order_invoice_list?.length) return data.order_invoice_list[0];

    const rawItems = data?.invoice_items;
    if (!Array.isArray(rawItems) || !rawItems.length) return null;

    const first = rawItems[0];
    const total = rawItems.reduce(
      (sum: number, i: any) => sum + this.extractPriceFromString(i?.item_amount),
      0,
    );

    return {
      invoice_number: this.apiData?.invoice_number || '',
      order_number: this.apiData?.order_number || '',
      invoice_status: first?.invoice_status || this.apiData?.invoice_status || '',
      delivery_date: this.apiData?.delivery_date || '',
      delivery_type: this.apiData?.delivery_type || '',
      tracking_dt: this.apiData?.tracking_dt || '',
      order_rating_status: this.apiData?.order_rating_status || '',
      invoice_rating: this.apiData?.invoice_rating ?? 0,
      invoice_items: rawItems.map((i: any) => ({
        ...i,
        image_url: i.image_url || i.part_image || '',
        item_id: String(i.item_id ?? ''),
        total_quantity: String(i.total_quantity ?? ''),
        available_quantity: i.available_quantity ?? Number(i.total_quantity ?? 0),
        booking_amount: String(i.booking_amount ?? ''),
      })),
      invoice_amount: total,
      seller_detail: data.seller_detail,
      order_address: data?.order_address || this.apiData?.order_address || [],
      invoice_pdf_path: this.apiData?.invoice_pdf_path || '',
      total_quantity: '',
      booking_amount: '',
      generated_pin: this.apiData?.generated_pin || '',
      integration_id: '',
    } as ApiOrderResponse;
  }

  getorderlist() {
    this.loaderService.show();
    const payload: any = {
      action_type: this.apiData.invoice_number ? 'invoice_items' : 'All Orders',
    };

    if (this.apiData.invoice_number) {
      payload.invoice_number = this.apiData.invoice_number;
      payload.invoice_status = this.apiData.invoice_status || '';
    } else if (this.apiData.order_number) {
      payload.order_number = this.apiData.order_number;
    }

    this.apiService.getorderlist(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.document_type = 'Invoice';
          const invoiceData = res.success
            ? this.normalizeInvoiceResponse(res.data)
            : null;

          if (invoiceData) {
            this.chekcInvoice_umnber = invoiceData.invoice_number;
            this.isLock = invoiceData;
            this.orderDetail = this.mapApiResponseToOrderDetail(invoiceData);
          }
          console.log('Mapped Order Detail:', this.orderDetail);
          this.deleveryType = this.orderDetail?.isStorePickup || false;

          if (
            !this.orderDetail.status ||
            this.orderDetail.status.length === 0
          ) {
            this.orderDetail.status = [];
          }

          this.getorderstatusDetails();
          this.orderInvoiceprice();
        } else {
          console.warn('No order data received or invalid response structure');
          this.orderDetail = {
            id: this.apiData?.order_number || 'Unknown',
            invoiceNo: this.apiData?.invoice_number || 'Unknown',
            orderNo: this.apiData?.order_number || 'Unknown',
            status: [],
            items: [],
            billingAddress: { name: '', pincode: '', address: '' },
            shippingAddress: { name: '', pincode: '', address: '' },
            sellerAddress: { name: '', pincode: '', address: '' },
            storeAddress: { name: '', pincode: '', address: '' },
            priceBreakdown: {
              subtotal: 0,
              discount: 0,
              platformCharges: 0,
              deliveryCharges: 0,
              gst: 0,
              total: 0,
            },
            canCancel: false,
            isStorePickup: false,
          };
        }
        this.loaderService.hide();
      },
      error: (error: any) => {
        console.error('Error fetching order list:', error);
        this.notificationService.error(
          error?.error?.data?.msg ||
          'Failed to load order details. Please try again.',
        );
        this.loaderService.hide();
        this.orderDetail = {
          id: this.apiData?.order_number || 'Unknown',
          invoiceNo: this.apiData?.invoice_number || 'Unknown',
          orderNo: this.apiData?.order_number || 'Unknown',
          status: [],
          items: [],
          billingAddress: { name: '', pincode: '', address: '' },
          shippingAddress: { name: '', pincode: '', address: '' },
          sellerAddress: { name: '', pincode: '', address: '' },
          storeAddress: { name: '', pincode: '', address: '' },
          priceBreakdown: {
            subtotal: 0,
            discount: 0,
            platformCharges: 0,
            deliveryCharges: 0,
            gst: 0,
            total: 0,
          },
          canCancel: false,
          isStorePickup: false,
        };
      },
    });
  }

  initiatePayment(order: any) {
    const invoiceId = order?.invoice?.invoice_id;
    if (!order?.id && !invoiceId) {
      console.error('Order number not available');
      return;
    }

    const paymentPayload = invoiceId
      ? {
        invoice_id: Number(invoiceId),
        ...(order?.payment_flow ? { payment_flow: order.payment_flow } : {}),
      }
      : {
        order_number: order?.id,
        is_retry_payment: true,
        ...(order?.payment_flow ? { payment_flow: order.payment_flow } : {}),
      };

    this.sub.add(
      this.paymentService.getEncryptedPaymentData(paymentPayload).subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            this.ccavenue = response?.data;
            let encRequest = response?.data;
            let ccavenuForm: FormGroup = this.formBuilder.group({
              encRequest: [encRequest],
              access_code: [this.ccavenue?.access_code],
            });
            this.payWithCcavenu(ccavenuForm);
          } else {
            console.error('Payment failed:', response.error);
          }
        },
        error: (error: any) => {
          this.notificationService.error(
            error?.error?.data?.msg ||
            'Failed to initiate payment. Please try again.',
          );
          console.error('Payment service error:', error);
        },
      }),
    );
  }

  payWithCcavenu(formGroup: FormGroup) {
    if (!this.ccavenue || !this.ccavenue.ccavenue_url) {
      console.error('CCavenue configuration not available');
      return;
    }

    const myform = document.createElement('form');
    myform.method = 'POST';

    const baseUrl = this.ccavenue.ccavenue_url;
    const separator = baseUrl.includes('?') ? '&' : '?';
    myform.action = `${baseUrl}${separator}encRequest=${this.ccavenue.encRequest}&access_code=${this.ccavenue.access_code}`;

    console.log('Form action URL:', myform.action);

    let input: any;
    for (const [key, value] of Object.entries(formGroup.value)) {
      input = document.createElement('input');
      input.name = key;
      input.id = key;
      input.value = value;
      myform.appendChild(input);
    }

    myform.style.display = 'none';
    document.body.appendChild(myform);
    console.log('Form submitted to:', myform.action);
    myform.submit();
  }

  retryPaymentMethod(order: any): boolean {
    if (!order?.items?.length) return false;
    return order.items.some(
      (item: any) => item.available_quantity >= item.total_quantity,
    );
  }

  gotoProductList() {
    localStorage.removeItem('category');
    localStorage.removeItem('mostSearchedModel');

    localStorage.removeItem('filterState');
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('MyGarageData');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('product_categoryType');
    localStorage.removeItem('product_category');
    this.router.navigate(['/product-list']);
  }

  moveToCart(order: any) {
    console.log('Moving order to cart:', order);

    if (!order?.items || order?.items?.length === 0) {
      console.error('No items found in the order');
      return;
    }

    const cartItems = order?.items?.map((item: any) => ({
      part_number: item?.inventory_id,
      delivery_type: order?.deliveryType,
      part_type: item?.part_type,
      quantity: item?.total_quantity,
    }));

    const payload = {
      offset: 0,
      limit: 10,
      action_type: 'ADD_DATA',
      validate_request: true,
      source: 'Online',
      part_number_list: cartItems.map((item: any) => item.part_number),
      cart_items: cartItems,
    };

    this.loaderService.show();

    this.apiService.addToCart(payload).subscribe({
      next: (res) => {
        this.loaderService.hide();
        console.log('Items successfully added to cart:', res);
      },
      error: (err) => {
        this.notificationService.error(
          err?.error?.data?.msg ||
          'Failed to add items to cart. Please try again.',
        );
        this.loaderService.hide();
        console.error('Error adding items to cart:', err);
      },
    });
  }

  knowMore() {
    this.router.navigate(['/terms-condition'], {
      queryParams: { tab: 'returns' },
    });
  }

  transformApiStatusesToOrderStatus(apiStatuses: any[]): OrderStatus[] {
    if (!apiStatuses || !Array.isArray(apiStatuses)) {
      return [];
    }

    const formatDate = (timestamp: string): string => {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    const formatTime = (timestamp: string): string => {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
        });
      } catch (error) {
        console.error('Error formatting time:', error);
        return '';
      }
    };

    const normalizeStatusName = (statusName: string): string => {
      const statusMap: { [key: string]: string } = {
        'Order Confirmed': 'Order Confirmed',
        'Ready to Dispatch': 'Ready to Dispatch',
        'Out for Delivery': 'Out for Delivery',
      };
      return statusMap[statusName] || statusName;
    };

    const filteredStatuses = apiStatuses.filter((apiStatus: any) => {
      const statusName = (apiStatus?.status || '').toLowerCase();
      return !statusName.includes('settlement');
    });

    return filteredStatuses.map((apiStatus: any) => {
      const statusName = apiStatus.status || '';
      const hasTimestamp =
        apiStatus.timestamp &&
        apiStatus.timestamp !== null &&
        apiStatus.timestamp !== 'null';
      const normalizedStatus = normalizeStatusName(statusName);

      return {
        stage: normalizedStatus as any,
        isCompleted: hasTimestamp,
        date: hasTimestamp ? formatDate(apiStatus.timestamp) : '',
        description: hasTimestamp
          ? `${statusName} completed`
          : `${statusName} pending`,
        timestamp: hasTimestamp ? apiStatus.timestamp : '',
        icon: this.getStatusIconType(statusName),
      };
    });
  }

  private getStatusIconType(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
      'order placed': 'order-placed',
      'order confirmed': 'order-placed',
      'ready to dispatch': 'shipped',
      'in-transit': 'in-transit',
      'in transit': 'in-transit',
      'out for delivery': 'out-for-delivery',
      delivered: 'delivered',
      'order cancelled': 'order-cancelled',
      'return requested': 'return-requested',
      'picked up': 'pickup',
      'refund credited': 'refund-credited',
      'payment pending': 'payment-pending',
      'payment successful': 'payment-successful',
      'payment failed': 'payment-failed',
      'order ready': 'order-ready',
      pending: 'order-placed',
      shipped: 'shipped',
    };
    return iconMap[statusLower] || 'order-placed';
  }

  getorderstatusDetails() {
    const invoiceNumber =
      this.apiData?.invoice_number || this.chekcInvoice_umnber || '';
    if (!invoiceNumber || this.orderDetail?.isBooking || this.isBookingFlow) {
      return;
    }
    this.loaderService.show();
    const payload: any = {
      invoice_number: this.apiData.invoice_number
        ? this.apiData.invoice_number
        : this.chekcInvoice_umnber
          ? this.chekcInvoice_umnber
          : '',
      invoice_line_list_id: this.orderDetail.items.map(
        (item: any) => item.item_id,
      ),
      get_all_status: false,
    };

    this.apiService.orderListDetails(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.orderStatuslistDetails = res.data;

          let statusArray: any[] = [];
          if (Array.isArray(res.data)) {
            statusArray = res.data;
          } else if (res.data?.statuses && Array.isArray(res.data.statuses)) {
            statusArray = res.data.statuses;
          } else if (res.data?.status && Array.isArray(res.data.status)) {
            statusArray = res.data.status;
          } else if (
            res.data?.order_status &&
            Array.isArray(res.data.order_status)
          ) {
            statusArray = res.data.order_status;
          }

          if (statusArray.length > 0) {
            const transformedStatuses =
              this.transformApiStatusesToOrderStatus(statusArray);
            if (this.orderDetail) {
              this.orderDetail.status = transformedStatuses;
              this.cdr.detectChanges();
            } else {
              setTimeout(() => {
                if (this.orderDetail) {
                  this.orderDetail.status = transformedStatuses;
                  this.cdr.detectChanges();
                }
              }, 100);
            }
          } else {
            console.warn('No status array found in API response:', res.data);
          }

          console.log('orderStatuslistDetails', this.orderStatuslistDetails);
          console.log('Transformed statuses:', this.orderDetail?.status);
          this.loaderService.hide();
        }
      },
      error: (error: any) => {
        this.notificationService.error(
          error?.error?.data?.msg ||
          'Failed to load order status details. Please try again.',
        );
        console.error('Error fetching order list:', error);
        this.loaderService.hide();
      },
    });
  }

  seeAllUpdatesStatus() {
    if (this.orderDetail?.isBooking || this.isBookingFlow) {
      this.popupStatusList = this.orderDetail?.status || [];
      this.cdr.detectChanges();
      this.scheduleMeasurement();
      return;
    }
    this.loaderService.show();
    const payload: any = {
      invoice_number: this.apiData.invoice_number
        ? this.apiData.invoice_number
        : this.chekcInvoice_umnber
          ? this.chekcInvoice_umnber
          : '',
      invoice_line_list_id: this.orderDetail.items.map(
        (item: any) => item.item_id,
      ),
      get_all_status: true,
    };

    this.apiService.orderListDetails(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          let statusArray: any[] = [];
          if (Array.isArray(res.data)) {
            statusArray = res.data;
          } else if (res.data?.statuses && Array.isArray(res.data.statuses)) {
            statusArray = res.data.statuses;
          } else if (res.data?.status && Array.isArray(res.data.status)) {
            statusArray = res.data.status;
          } else if (
            res.data?.order_status &&
            Array.isArray(res.data.order_status)
          ) {
            statusArray = res.data.order_status;
          }

          this.seeAllUpdatesStatusDetails = res.data;

          if (statusArray.length > 0) {
            this.popupStatusList =
              this.transformApiStatusesToOrderStatus(statusArray);
          } else {
            this.popupStatusList = [];
          }

          console.log(
            'seeAllUpdatesStatusDetails',
            this.seeAllUpdatesStatusDetails,
          );
          console.log('popupStatusList', this.popupStatusList);
          this.cdr.detectChanges();
          this.scheduleMeasurement();
          this.loaderService.hide();
        }
      },
      error: (error: any) => {
        this.notificationService.error(
          error?.error?.data?.msg ||
          'Failed to load all status updates. Please try again.',
        );
        console.error('Error fetching order list:', error);
        this.loaderService.hide();
        this.popupStatusList = [];
      },
    });
  }

  orderInvoiceprice() {
    const orderNumber =
      this.apiData?.order_number ||
      this.orderDetail?.orderNo ||
      this.orderDetail?.id;

    if (!orderNumber) {
      return;
    }
    this.loaderService.show();

    // Collect integration ids
    const integrationIds = (
      this.orderDetail?.items ||
      this.apiData?.line_items ||
      this.apiData?.invoice_items ||
      []
    )
      .map((item: any) => item?.integration_id)
      .filter(Boolean);

    // Determine booking_type / document_type: 'ENQUIRY' | 'TAX_INVOICE' | 'BOOKING'
    let docType = 'BOOKING';
    const isEnq =
      (typeof this.isEnquiry === 'function' && this.isEnquiry()) ||
      this.apiData?.isEnquiry ||
      String(this.apiData?.document_type || '').toUpperCase() === 'ENQUIRY' ||
      String(this.document_type || '').toUpperCase() === 'ENQUIRY' ||
      String(this.apiData?.booking_number || '').toUpperCase().startsWith('ENQ-') ||
      String(this.apiData?.order_number || '').toUpperCase().startsWith('ENQ-');

    if (isEnq) {
      docType = 'ENQUIRY';
    } else if (
      this.apiData?.invoice_number ||
      (this.apiData?.invoice_items && this.apiData.invoice_items.length > 0) ||
      String(this.apiData?.document_type || '').toUpperCase() === 'TAX_INVOICE' ||
      String(this.document_type || '').toUpperCase() === 'INVOICE'
    ) {
      docType = 'TAX_INVOICE';
    } else if (
      this.apiData?.booking_number ||
      String(this.apiData?.document_type || '').toUpperCase() === 'BOOKING' ||
      this.orderDetail?.isBooking ||
      this.isBookingFlow
    ) {
      docType = 'BOOKING';
    }

    // Determine status
    let orderStatus = '';

    if (docType === 'ENQUIRY') {
      orderStatus = this.apiData?.order_status || '';
    } else if (docType === 'TAX_INVOICE' || docType === 'BOOKING') {
      orderStatus = this.apiData?.booking_status || '';
    }

    const payload: any = {
      action_type: '',
      order_number: orderNumber,
      integration_id_list: integrationIds,
      booking_type: docType,
      // document_type: docType,
      status: orderStatus,
      // order_status: orderStatus,
    };

    if (this.apiData?.invoice_number) {
      payload.invoice_number = this.apiData.invoice_number;
    }
    if (this.apiData?.booking_number) {
      payload.booking_number = this.apiData.booking_number;
    }

    this.apiService.orderInvoiceprice(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          console.log('orderInvoiceprice', res.data);
          if (!this.orderDetail) {
            this.orderDetail = {} as any;
          }
          if (!this.orderDetail.estimatedDelivery && !['Delivered', 'Picked Up', 'Order Cancelled'].includes(this.orderDetail.invoice_status || '')) {
            this.orderDetail.estimatedDelivery = this.getFutureTwoDaysDate();
          }
          if (res.data?.price_breakup) {
            const pb = res.data.price_breakup;
            const subtotal = this.extractPriceFromString(
              pb.base_price ?? pb.taxable_value ?? pb.sub_total ?? 0,
            );
            const discount = this.extractPriceFromString(
              pb.total_discount ?? pb.discount ?? pb.discount_on_base_price ?? 0,
            );
            const platform = this.extractPriceFromString(
              pb.platform_charges ?? pb.platformCharges ?? 0,
            );
            const delivery = this.extractPriceFromString(
              pb.delivery_charges_with_gst ?? pb.delivery_charges ?? pb.deliveryCharges ?? 0,
            );
            const gst = this.extractPriceFromString(
              pb.gst_amount ?? pb.gst ?? 0,
            );
            const total = this.extractPriceFromString(
              pb.order_total ?? pb.rounded_total ?? pb.total_amount ?? pb.total ?? 0,
            );
            const scrapBooking = this.extractPriceFromString(
              (pb.scrap_booking_amount && Number(pb.scrap_booking_amount) > 0)
                ? pb.scrap_booking_amount
                : (pb.paid_booking_amount && Number(pb.paid_booking_amount) > 0)
                  ? pb.paid_booking_amount
                  : this.orderDetail?.priceBreakdown?.scrap_booking_amount ?? 0,
            );
            const pendingAmount = this.extractPriceFromString(
              pb.remaining_booking_amount ?? pb.pending_amount ?? 0,
            );
            const roundoff = this.extractPriceFromString(
              pb.roundoff_amount ?? 0,
            );
            const roundedTotal = this.extractPriceFromString(
              pb.rounded_total ?? total,
            );
            const tcsAmount = this.extractPriceFromString(
              pb.tcs_amount ?? 0,
            );

            this.orderDetail.priceBreakdown = {
              ...(this.orderDetail.priceBreakdown || {}),
              ...pb,
              subtotal,
              base_price: subtotal,
              taxable_value: this.extractPriceFromString(pb.taxable_value ?? subtotal),
              discount,
              total_discount: discount,
              platformCharges: platform,
              platform_charges: platform,
              deliveryCharges: delivery,
              delivery_charges: delivery,
              delivery_charges_with_gst: delivery,
              gst,
              gst_amount: gst,
              total,
              order_total: total,
              total_amount: total,
              rounded_total: roundedTotal,
              roundoff_amount: roundoff,
              scrap_booking_amount: scrapBooking,
              paid_booking_amount: scrapBooking,
              pending_amount: pendingAmount,
              remaining_booking_amount: pendingAmount,
              tcs_amount: tcsAmount,
              // price_breakup mapped
            };
          }

          // Find addresses based on order_address_type
          const billingAddressData = res.data.order_address?.find(
            (addr: any) => addr?.order_address_type === 'billing_address',
          );
          const shippingAddressData = res.data.order_address?.find(
            (addr: any) => addr?.order_address_type === 'shipping_address',
          );

          // Map billing address
          if (billingAddressData?.address_1 || billingAddressData?.company_name || billingAddressData?.receiver_name) {
            this.orderDetail.billingAddress = {
              name:
                billingAddressData?.company_name ||
                billingAddressData?.receiver_name ||
                this.userData?.first_name + ' ' + this.userData?.last_name ||
                '',
              pincode: billingAddressData?.pincode || '',
              address:
                `${billingAddressData?.address_1 || ''} ${billingAddressData?.city || ''} ${billingAddressData?.state || ''}`.trim(),
              gst: billingAddressData?.gstin_number || undefined,
              type:
                (billingAddressData?.address_type as
                  | 'Home'
                  | 'Work'
                  | 'Other') || 'Work',
            };
          } else if (!this.orderDetail.billingAddress?.address) {
            const userBilling = this.getUserBillingAddress();
            if (userBilling.address || userBilling.name) {
              this.orderDetail.billingAddress = {
                ...(this.orderDetail.billingAddress || {}),
                ...userBilling,
                name: this.orderDetail.billingAddress?.name || userBilling.name,
              };
            }
          }

          // Map shipping address
          if (shippingAddressData?.address_1 || shippingAddressData?.receiver_name || shippingAddressData?.company_name) {
            this.orderDetail.shippingAddress = {
              name:
                shippingAddressData?.receiver_name ||
                shippingAddressData?.company_name ||
                this.userData?.first_name + ' ' + this.userData?.last_name ||
                '',
              pincode: shippingAddressData?.pincode || '',
              address:
                `${shippingAddressData?.address_1 || ''} ${shippingAddressData?.city || ''} ${shippingAddressData?.state || ''}`.trim(),
              gst: shippingAddressData?.gstin_number || undefined,
              type:
                (shippingAddressData?.address_type as
                  | 'Home'
                  | 'Work'
                  | 'Other') || 'Home',
            };
          } else if (!this.orderDetail.shippingAddress?.address && !this.orderDetail.isStorePickup && !this.deleveryType) {
            const userShipping = this.getUserShippingAddress();
            if (userShipping.address || userShipping.name) {
              this.orderDetail.shippingAddress = {
                ...(this.orderDetail.shippingAddress || {}),
                ...userShipping,
                name: this.orderDetail.shippingAddress?.name || userShipping.name,
              };
            }
          }

          const storeAddressData = res.data.order_address?.find(
            (addr: any) => addr?.order_address_type === 'store_address',
          );
          if (storeAddressData || res.data?.seller_detail) {
            const sellerAddr = this.extractSellerAddress(res.data, this.orderDetail.items);
            if (sellerAddr.address || sellerAddr.name) {
              this.orderDetail.sellerAddress = sellerAddr;
              this.orderDetail.storeAddress = sellerAddr;
            }
          }

          this.cdr.detectChanges();
        }
        this.loaderService.hide();
      },
      error: (error: any) => {
        this.notificationService.error(
          error?.error?.data?.msg ||
          'Failed to fetch order list. Please try again.',
        );
        console.error('Error fetching order list:', error);
        this.loaderService.hide();
      },
    });
  }
}